<template>
  <div>
    <!-- 保存・取得中は画面全体を覆う（サブタスクのパネルが保存中のときも、パネルからの通知でここを出す） -->
    <div v-if="loading" class="loading-mask">
      Waiting...
    </div>

    <PageHeader :title="typeName">
      <template #title-header-content>
        <div class="flex-1 flex items-center justify-end gap-x-2 mr-2">

          <!-- 削除ボタン（未保存・一時保存・差戻のとき。誰でも押せる。承認者の画面には出さない）
               ほかのボタンと離して左端に置き、赤以外の色にする（隣だと押し間違えるため。ユーザー指示 2026-09-23）
               保存していた内容を消して状況を「削除」にし、一覧へ戻る。復活はメインタスク・サブタスクのボタンから開き直すだけ（メインタスクが未完了のとき） -->
          <PrimaryButton v-if="!isReadOnly && !isApprover" text="削除" size="xs" class="w-20 justify-center mr-auto bg-gray-700 hover:bg-gray-800 border border-white" @click="deleteRequest()">
            <Icon slot="before" iconName="Trash" iconType="outline" :strokeWidth="2" class="h-3.5 w-3.5" />
          </PrimaryButton>

          <!-- 全て開く -->
          <div class="w-24 text-center font-bold text-sm text-gray-50 cursor-pointer hover:text-gray-300 whitespace-nowrap" @click="allOpen()">
            <span class="border-b border-gray-50 px-2">全て開く</span>
          </div>

          <!-- 全て閉じる -->
          <div class="w-24 text-center font-bold text-sm text-gray-50 cursor-pointer hover:text-gray-300 whitespace-nowrap ml-2 mr-6" @click="allClose()">
            <span class="border-b border-gray-50 px-2">全て閉じる</span>
          </div>

          <!-- メインタスク遷移ボタン -->
          <PrimaryButton text="メインタスクへ" class="w-32 px-1 bg-teal-600 hover:bg-teal-700" @click="toMainTask()">
            <Icon slot="before" iconName="ViewGrid" iconType="solid" :strokeWidth="2" class="h-4 w-4" />
          </PrimaryButton>

          <!-- 状況で出すボタンを変える（constantsAKBS の AKBS_STATUS を参照）
               ・未保存・一時保存・差戻・削除：申請、一時保存（削除は左端）
               ・承認待ち：申請取消、差戻、AKBSへ登録（内容は閲覧のみ）
               ・AKBS登録済み：なし（閲覧のみ） -->
          <template v-if="!isReadOnly">
            <!-- 申請ボタン -->
            <PrimaryButton text="申請" class="w-32" @click="save(true)">
              <Icon slot="before" iconName="PaperAirplane" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
            </PrimaryButton>

            <!-- 一時保存ボタン -->
            <PrimaryButton text="一時保存" class="w-32 bg-yellow-600 hover:bg-yellow-700" @click="save(false)">
              <Icon slot="before" iconName="Save" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
            </PrimaryButton>
          </template>

          <!-- AKBS登録済み（承認済み）はボタンの代わりに印（閲覧のみと分かるように） -->
          <div v-if="isRegistered" class="registered-mark">
            <Icon iconName="CheckCircle" iconType="solid" class="h-5 w-5 mr-1" />AKBSに登録済み
          </div>

          <template v-if="isPending">
            <!-- 申請取消ボタン（承認待ちを一時保存に戻して、また直せるようにする）
                 承認待ち一覧の「確認」から開いたとき（承認者）は申請者の操作なので出さない -->
            <PrimaryButton v-if="!isApprover" text="申請取消" class="w-32 bg-gray-500 hover:bg-gray-600" @click="cancelApply()">
              <Icon slot="before" iconName="X" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
            </PrimaryButton>

            <!-- 差戻・AKBSへ登録は承認の操作なので、AKBS申請一覧の「確認」から開き、かつAKBS権限がある人にだけ出す -->
            <template v-if="canApprove">
              <!-- 差戻ボタン（承認者が申請者へ戻す。押したあと確認ダイアログを出す） -->
              <PrimaryButton text="差戻" class="w-32 bg-yellow-600 hover:bg-yellow-700" @click="sendBack()">
                <Icon slot="before" iconName="Reply" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
              </PrimaryButton>

              <!-- AKBSへ登録ボタン（承認。変更したところだけ本番のAKBSへ書く） -->
              <PrimaryButton text="AKBSへ登録" class="w-36 bg-pink-600 hover:bg-pink-700" @click="register()">
                <Icon slot="before" iconName="CheckCircle" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
              </PrimaryButton>
            </template>
          </template>

          <!-- 一覧へ移るドロップダウン（見積画面の「見積リスト」と同じ部品。一番右に置く）
               ボタンは文字の幅のまま、開いたリストだけ項目名が切れない幅にする -->
          <DropdownButton text="一覧" size="font-bold text-white rounded bg-transparent" :menus="listMenu" :menuWidth="160" class="ml-4" @select="selectList">
            <Icon slot="before" iconName="ViewList" class="h-4 w-4" />
            <Icon slot="after" iconName="ChevronDown" class="h-4 w-4" />
          </DropdownButton>
        </div>
      </template>
    </PageHeader>
    <!-- 画面を狭めても 1000px より縮めない（それより狭いときは画面全体を横にスクロールする） -->
    <main class="page-main absolute top-20 w-screen bottom-4 -mt-2 leading-8">
      <div class="mx-auto px-2 sm:px-4 h-full">
        <div ref="form" class="h-full bg-gray-200 rounded-md border border-gray-300 overflow-auto px-2 pb-4">

          <!-- 注意書き（承認者（approver=true）が承認待ちを開いたときは、申請取消は申請者の操作なので何も出さない） -->
          <div v-if="isRegistered" class="mt-3 px-1 text-xs font-bold text-red-700 leading-5">
            この申請はAKBSへ登録済みです。内容の変更や再登録はできません。
          </div>
          <div v-else-if="deletedFinal" class="mt-3 px-1 text-xs font-bold text-red-700 leading-5">
            この申請は削除済みで、メインタスクも完了しているため、復活できません（閲覧のみ）。
          </div>
          <!-- 承認者として開いたが自分の申請（作成者本人は承認できない） -->
          <div v-else-if="isPending && isApprover && isMyRequest" class="mt-3 px-1 text-xs font-bold text-red-700 leading-5">
            自分が作成した申請は承認できません。他の承認者に確認してもらってください（閲覧のみ）。
          </div>
          <div v-else-if="isPending && !isApprover" class="mt-3 px-1 text-xs font-bold text-red-700 leading-5">
            この申請は承認待ちです。内容を直すときは、先に右上の「申請取消」を押してください。
          </div>
          <div v-else-if="!isPending" class="mt-3 px-1 text-xs font-bold text-red-700 leading-5">
            <span class="text-red-500">*</span> の項目は必須です。一時保存の場合は必須項目が未入力でも保存できます。
          </div>

          <!-- 差戻理由（承認者が差戻のときに書く。過去の分は文字列で積んであるので閲覧のみで下に出す）
               承認者で承認待ちのときは今回の入力欄を出す。過去の分が1件も無く、書く立場でもなければ枠ごと出さない
               他の大項目（藍色）と見分けがつくよう、差戻ボタンと同じ黄色系にする -->
          <div v-if="canWriteReturnReason || val.returnReason" class="mt-4 bg-white border-2 border-yellow-500 rounded shadow">
            <div class="text-sm font-bold text-yellow-900 bg-yellow-200 rounded-t px-3 py-2 border-b border-yellow-500">
              差戻理由
            </div>
            <div class="px-3 py-3">
              <!-- 今回の分（差戻ボタンを押したときに、ユーザー名・日時を付けて過去の分の上に積む） -->
              <WmsTextInput v-if="canWriteReturnReason" name="newReturnReason" caption="今回の差戻理由" v-model="newReturnReason" :multiline="true" :rows="3" class="mb-3" />

              <!-- 過去の分（新しいものが上。改行をそのまま出す） -->
              <template v-if="val.returnReason">
                <label v-if="canWriteReturnReason" class="block text-xs font-bold text-gray-700">これまでの差戻理由</label>
                <div class="view whitespace-pre-wrap" :class="canWriteReturnReason ? 'mt-1' : ''">{{ val.returnReason }}</div>
              </template>
            </div>
          </div>

          <!-- 連絡事項とメモ
               連絡事項はメインタスク（雇い入れ）のもの。ここでは直せない（契約詳細入力画面からの変更内容は、下の「契約詳細の申請」の枠に出す）
               メモはこの申請のもの。承認者・総務などに伝えたいことを書く（閲覧のみのときは直せない）
               何か書いてあるときは見落とされないよう、見出しに札を出し、欄をオレンジ色にする（閲覧のみの灰色の欄だと気づきにくいため） -->
          <div class="mt-4 bg-white border border-indigo-200 rounded shadow">
            <div class="text-sm font-bold text-indigo-900 bg-indigo-50 rounded-t px-3 py-2 border-b border-indigo-200">
              連絡事項・メモ
              <span v-if="hasMemo" class="memo-badge">メモあり</span>
            </div>
            <div class="px-3 py-3">
              <template v-if="rp.employmentRemarks">
                <label class="block text-xs font-bold text-gray-700">連絡事項（メインタスクの内容。ここでは変更できません）</label>
                <!-- URL は押せるリンクにする（別タブ） -->
                <div class="view whitespace-pre-wrap break-all mt-1 mb-3"><template v-for="(p, pi) in remarkParts"><a v-if="p.url" :key="'rm' + pi" :href="p.text" target="_blank" rel="noopener" class="text-indigo-700 underline">{{ p.text }}</a><span v-else :key="'rm' + pi">{{ p.text }}</span></template></div>
              </template>
              <WmsTextInput name="memo" caption="メモ（承認者・総務などに伝えたいこと）" v-model="val.memo" :multiline="true" :rows="2" :disabled="isReadOnly" :class="hasMemo ? 'memo-filled' : ''" />
            </div>
          </div>

          <!-- 契約詳細の申請のお知らせ（この清掃員が働いている契約に、契約終了・変更の申請があるとき）
               同じ清掃員の申請は AKBS登録済みまで1つにまとめるので、終了する契約をこの申請の中で作業終了にしてもらう
               表は契約詳細の申請の今の内容（開くたびに取る）。出すのは、この申請（req_akbs.contract_notes）に取り消しでないメモがある契約詳細の申請だけ（バック）
               「変更点」の列は、そのメモの文のうち行ごとの変更点（どの行のどの欄を変えたか。備考・勤務形態など）。メモの見出しは「内容」と同じなので出さない（ユーザー 2026-09-24） -->
          <div v-if="contractNoticeRows.length" class="con-notice" :class="contractNoticeTodos.length ? 'todo' : ''">
            <div class="con-notice-title">
              契約詳細の変更が申請されています（{{ akbs.nameStaff ? akbs.nameStaff + 'さん' : 'この清掃員' }}が働いている契約）
              <span v-if="contractNoticeTodos.length" class="con-notice-count">作業終了がまだの契約 {{ contractNoticeTodos.length }}件</span>
            </div>
            <div v-if="contractNoticeTodos.length" class="con-notice-lead">
              終了する契約があります。下の雇用指示で、その契約を「作業終了」にしてください。
            </div>
            <table v-if="contractNoticeRows.length" class="con-notice-table">
              <thead>
                <tr>
                  <th>物件名</th>
                  <th>契約コード</th>
                  <th>内容</th>
                  <th>変更点</th>
                  <th>この申請の雇用指示</th>
                  <th>契約詳細の申請</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(n, ni) in contractNoticeRows" :key="'cn' + ni">
                  <td>{{ n.siteName }}</td>
                  <td class="text-center font-bold">{{ n.cdContract }}</td>
                  <td>
                    <template v-if="n.mode === 'end'">
                      <span class="con-notice-end">契約終了</span>
                      <span class="con-notice-date">終了日 {{ slash(n.endDate) || '未入力' }}</span>
                    </template>
                    <template v-else>
                      {{ n.modeName }}
                      <span v-if="n.startDate || n.changeDate" class="con-notice-date">{{ slash(n.startDate || n.changeDate) }} から</span>
                      <span v-if="n.newSiteCode" class="con-notice-date">物件コード {{ n.siteCode }} ⇒ {{ n.newSiteCode }}</span>
                    </template>
                  </td>
                  <!-- 行ごとの変更点（契約詳細入力画面の保存時にバックが書いたメモから。1行ずつ） -->
                  <td class="con-notice-details">
                    <div v-for="(d, di) in n.details || []" :key="'cd' + di">{{ d }}</div>
                  </td>
                  <td>
                    <span v-if="n.state === 'todo'" class="con-notice-todo">作業終了がまだです</span>
                    <span v-else-if="n.state === 'ok'">終了日 入力済み</span>
                    <span v-else-if="n.state === 'none'">この契約の雇用指示はありません</span>
                    <span v-else>必要なら、下の雇用指示を変更してください</span>
                  </td>
                  <td>
                    {{ n.formStatus }}
                    <a href="#" class="con-notice-open" @click.prevent="openContractForm(n)">契約詳細入力画面を開く</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 承認待ち・AKBS登録済みは閲覧のみ（fieldset の disabled で中の入力欄・ボタンをまとめて止める） -->
          <fieldset :disabled="isReadOnly" class="readonly-area" :class="isReadOnly ? 'readonly' : ''">

          <!-- 大項目ごとにアコーディオン。大項目は横に並べ（清掃員基本情報・連絡先・…）、中の項目は縦に1列（項目名＋値）
               承認者が一度に見られるよう、タブには分けない（ユーザー指示 2026-10-07） -->
          <div class="sections">
          <div v-for="section in sections" :key="section.key" class="section-card mt-4 bg-white border border-indigo-200 rounded shadow" :class="opened[section.key] ? '' : 'closed'">

            <!-- 見出し（クリックで開閉） -->
            <div
              class="flex items-center cursor-pointer select-none text-sm font-bold text-indigo-900 bg-indigo-50 hover:bg-indigo-100 rounded-t px-3 py-2 transition-colors"
              :class="opened[section.key] ? 'border-b border-indigo-200' : ''"
              @click="toggle(section)"
            >
              <span class="mr-2 text-xs">{{ opened[section.key] ? '▼' : '▶' }}</span>
              {{ section.caption }}
              <!-- 開いていなくても直した箇所が分かるよう、件数を見出しに出す（承認者の確認用） -->
              <span v-if="changedCounts[section.key]" class="changed-count">（{{ changedCounts[section.key] }}箇所変更）</span>
              <!-- 承認（AKBS登録）のあとも登録済み一覧で直せることを帯に出す（ユーザー指示）
                   家族・緊急連絡先は全部、清掃員基本情報・連絡先は ★ の付いた項目（性別・生年月日・TEL・携帯TEL）だけ -->
              <span v-if="section.key === 'family' || section.key === 'emergency'" class="after-approval-note">承認後に登録済み一覧にて編集可能</span>
              <span v-else-if="section.key === 'staff' || section.key === 'address'" class="after-approval-note">★は承認後に登録済み一覧にて編集可能</span>
            </div>

            <!-- 中身 -->
            <div class="accordion px-3" :class="opened[section.key] ? 'open' : ''">

              <!-- 清掃員コード（表示のみ・変更不可） -->
              <div v-if="section.key === 'staff'" class="flex items-center gap-x-2 pt-3">
                <div class="text-xs font-bold text-gray-700">清掃員コード</div>
                <div class="text-lg font-bold text-indigo-900 tracking-wider leading-8">{{ akbs.staffDailyCleaning || '（未採番）' }}</div>
              </div>

              <!-- 給与関連の注意書き（シルバーパス金額は AKBS の給与計算では使わない。清掃員マスタに控えるだけ） -->
              <div v-if="section.key === 'wage'" class="pt-3 text-xs font-bold text-red-600">
                シルバーパス金額はAKBSの給与計算に反映されません
              </div>

              <!-- 再入社：前回の退職の内容（表示のみ。承認者が見られるように残す。入力欄は今回の退職用に空にしている） -->
              <div v-if="section.key === 'retire' && akbs.previousRetire" class="previous-retire mt-3">
                <div class="text-xs font-bold text-gray-700">前回の退職</div>
                <div class="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-sm">
                  <div v-for="item in items.retire" :key="'prev' + item.key" :class="isPreviousRetireWarning(item) ? 'text-red-700 font-bold' : 'text-gray-700'">
                    {{ item.caption }}：{{ previousRetireText(item) }}
                  </div>
                </div>
              </div>

              <!-- 明細（追加可能な大項目は複数行、それ以外は1行のみ） -->
              <div
                v-for="(row, index) in rows(section)"
                :key="index"
                :class="section.repeat && index ? 'border-t border-gray-200 mt-3' : ''"
              >
                <!-- 明細見出し（追加可能な大項目のみ） -->
                <div v-if="section.repeat" class="flex items-center pt-2">
                  <div class="text-xs font-bold text-gray-500">{{ index + 1 }}件目</div>
                  <Icon
                    iconName="Trash"
                    :clickable="true"
                    class="w-6 h-6 ml-auto mr-1 text-gray-400"
                    @click="removeRow(section, index)"
                  />
                </div>

                <!-- 項目は「項目名の列（薄い灰色）＋値」の表の形（登録済み一覧の清掃員情報と同じ見え方）。1行に2項目。
                     部品が持つ見出しは隠して、項目名はこちらの列に出す（fcap）。備考など幅いっぱいの項目は1行を使う -->
                <div class="form-grid">
                  <div
                    v-for="item in items[section.key]"
                    :key="item.key"
                    class="contents"
                    :class="[isChanged(section, item, index) ? 'changed' : '', isEmpty(item, row) ? 'required-empty' : '', isNg(section, item, index) ? 'ng' : '']"
                  >
                    <div class="fcap" :class="isFullWidth(item) ? 'full' : ''">
                      <div>
                        <Icon v-if="item.key === 'birthday'" iconName="Cake" iconType="solid" class="w-4 h-4 mr-1 text-gray-400 inline-block align-text-bottom" />
                        {{ plainCaption(item) }}
                        <span v-if="isRequired(item, row)" class="ml-1 text-red-500">*</span>
                        <!-- 承認後に登録済み一覧で直せる項目の印（帯の「★は承認後に…」と対応） -->
                        <span v-if="item.star" class="ml-1 text-red-600">★</span>
                        <!-- 見出しの補足（「保存時に半角に変換します」など）は項目名の下に小さく出す -->
                        <div v-if="captionNote(item)" class="fcap-note">{{ captionNote(item) }}</div>
                      </div>
                    </div>
                    <div class="fval" :class="isFullWidth(item) ? 'full' : ''">
                    <!-- 閲覧のみ（承認待ち・AKBS登録済み）は入力欄ではなく文字で出す
                         disabled の入力欄は Chrome で文字を選べないため、承認者が名前や住所をコピーできるようにする。チェックは disabled のまま -->
                    <div v-if="isReadOnly && item.type !== 'checkbox'" :class="item.class || 'w-52'">
                      <label class="block text-xs font-bold text-gray-700">{{ plainCaption(item) }}</label>
                      <div class="view mt-1" :class="item.type === 'textarea' ? 'whitespace-pre-wrap' : 'whitespace-nowrap'">{{ viewText(item, row) }}</div>
                      <!-- 和暦・年齢は枠の中には入れず、入力できるときと同じく枠の下に出す -->
                      <div v-if="subText(item, row)" class="mt-1 text-xs font-bold text-gray-500 leading-4 whitespace-nowrap">
                        {{ subText(item, row) }}
                      </div>
                    </div>

                    <!-- テキスト・電話番号・複数行（format 指定の項目は形式が違うと下に赤字で表示。電話番号・〒は入力後に半角にする） -->
                    <template v-else-if="item.type === 'text' || item.type === 'tel' || item.type === 'textarea'">
                      <div :class="item.class || 'w-52'">
                        <WmsTextInput
                          :name="section.key + item.key + index"
                          :caption="item.caption"
                          :value="row[item.key]"
                          @change="setText(item, row, $event)"
                          :multiline="item.type === 'textarea'"
                          :rows="item.rows || 3"
                          :required="isRequired(item, row)"
                          :autocomplete="item.format === 'zip' ? 'new-password' : 'off'"
                          @keydown.native.enter="nextField"
                          @compositionstart.native="kanaStart(item, row)"
                          @compositionupdate.native="kanaUpdate(item, row, $event)"
                          @compositionend.native="kanaEnd(item, row, $event)"
                          @input.native="kanaInput(item, row, $event)"
                        />
                        <div v-if="errText(item, row)" class="mt-1 text-xs font-bold text-red-500 leading-4 whitespace-nowrap">
                          {{ errText(item, row) }}
                        </div>
                      </div>

                      <!-- 郵便番号の横に住所を取得するボタン（連絡先は都道府県・市区町村・町名に分けて、緊急連絡先は住所1欄にまとめて入れる） -->
                      <div v-if="item.format === 'zip'" class="self-end h-9 flex items-center">
                        <PrimaryButton text="住所を取得" size="xs" class="bg-green-600 hover:bg-green-700" @click="fillAddress(section, row)">
                          <Icon slot="before" iconName="Search" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
                        </PrimaryButton>
                      </div>
                    </template>

                    <div
                      v-else-if="item.type === 'checkbox'"
                      class="flex items-center self-end h-9"
                      :class="[item.class || 'w-32', checkboxDisabled(item, row) ? 'opacity-50' : '']"
                      v-tooltip="checkboxTip(item, row)"
                    >
                      <input
                        type="checkbox"
                        :id="section.key + item.key + index"
                        :name="section.key + item.key + index"
                        v-model="row[item.key]"
                        :disabled="checkboxDisabled(item, row)"
                        class="w-4 h-4 cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label :for="section.key + item.key + index" class="ml-2 text-xs font-bold text-gray-700 cursor-pointer select-none whitespace-nowrap">
                        {{ item.caption }}
                      </label>
                    </div>

                    <div
                      v-else-if="item.type === 'number'"
                      class="flex items-end"
                      :class="item.class || 'w-52'"
                    >
                      <NumericTextInput2
                        :name="section.key + item.key + index"
                        :caption="item.caption"
                        v-model="row[item.key]"
                        :required="isRequired(item, row)"
                        class="flex-1"
                        @onEnterKey="nextField"
                      />
                      <span v-if="item.unit" class="ml-1 mb-1.5 text-xs font-bold text-gray-500 whitespace-nowrap">{{ item.unit }}</span>
                    </div>

                    <div
                      v-else-if="item.type === 'date'"
                      :class="item.class || 'w-52'"
                    >
                      <!-- 生年月日など、古い日付を打ちやすいブラウザ標準の日付入力 -->
                      <div v-if="item.native">
                        <label :for="section.key + item.key + index" class="flex items-center text-xs font-bold text-gray-700 relative">
                          <Icon v-if="item.key === 'birthday'" iconName="Cake" iconType="solid" class="w-4 h-4 mr-1 text-gray-500" />
                          {{ item.caption }}
                          <span v-if="isRequired(item, row)" class="ml-1 text-red-500"> * </span>
                        </label>
                        <div class="mt-1 flex relative">
                          <input
                            type="date"
                            :id="section.key + item.key + index"
                            :name="section.key + item.key + index"
                            :value="row[item.key]"
                            :max="today"
                            class="py-1.5 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm rounded border-gray-300"
                            @change="row[item.key] = $event.target.value"
                            @keydown.enter="nextField"
                          />
                        </div>
                      </div>

                      <WmsDateField
                        v-else
                        :name="section.key + item.key + index"
                        :caption="item.caption"
                        v-model="row[item.key]"
                        :clearable="true"
                        :required="isRequired(item, row)"
                        @change="dateFieldChanged(item)"
                        @keydown.native.enter="nextField"
                      />

                      <!-- 和暦・年齢（入力欄の下） -->
                      <div v-if="subText(item, row)" class="mt-1 text-xs font-bold text-gray-500 leading-4 whitespace-nowrap">
                        {{ subText(item, row) }}
                      </div>
                    </div>

                    <!-- 選択（v-model には選択肢の value の文字列が入る） -->
                    <div v-else-if="item.type === 'select'" :class="item.class || 'w-52'">
                      <label :for="section.key + item.key + index" class="block text-xs font-bold text-gray-700 relative">
                        {{ item.caption }}
                        <span v-if="isRequired(item, row)" class="absolute -top-0.5 ml-1 text-red-500"> * </span>
                      </label>
                      <div class="mt-1 flex relative">
                        <select
                          :id="section.key + item.key + index"
                          :name="section.key + item.key + index"
                          v-model="row[item.key]"
                          class="py-1.5 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm rounded border-gray-300"
                          @keydown.enter="nextField"
                        >
                          <option value=""></option>
                          <option v-for="s in (item.selections || [])" :key="s.value" :value="s.value">{{ s.text }}</option>
                        </select>
                      </div>
                    </div>

                    <WmsSuggestInput
                      v-else-if="item.type === 'suggest'"
                      :name="section.key + item.key + index"
                      :caption="item.caption"
                      v-model="row[item.key]"
                      :selectionItems="item.selections || []"
                      displayField="text"
                      :clearable="true"
                      :required="isRequired(item, row)"
                      :class="item.class || 'w-52'"
                      @onEnterKey="nextField"
                    />
                    </div>
                  </div>
                </div>
              </div>

              <!-- 追加ボタン（追加可能な大項目のみ） -->
              <div v-if="section.repeat" class="pt-2 pb-3">
                <PrimaryButton :text="`${section.caption}を追加`" size="xs" @click="addRow(section)">
                  <Icon slot="before" iconName="Plus" iconType="outline" :strokeWidth="2" class="h-4 w-4" />
                </PrimaryButton>
              </div>

            </div>

          </div>
          </div>

          <!-- 最低賃金を下回ったときだけ、その行の営業所（東京／千葉）の最低賃金マスタを出す（いつからいくら以上にすればよいかが分かるように） -->
          <div v-if="minWageNotice" class="min-wage-box">
            <div class="min-wage-title">{{ minWageNotice.pref }} の最低賃金</div>
            <div class="min-wage-text">{{ minWageNotice.name }} の時給が、{{ slash(minWageNotice.from) }} からの最低賃金（{{ Number(minWageNotice.minWage).toLocaleString() }}円）を下回っています。</div>
            <!-- 賃金を変えた行だけ止めているので、他の行が止まらない理由を添える -->
            <div class="min-wage-text">
              この申請で賃金変更を行っていない契約に関しては、<br />
              総務が一括で賃金を変更するのでそのままで大丈夫です。<br />
              賃金に1つでも変更がある場合は、こちらのデータが優先して保存されるので最低賃金を考慮して変更してください。
            </div>
            <table class="min-wage-table">
              <thead>
                <tr>
                  <th>適用開始日</th>
                  <th>適用終了日</th>
                  <th>最低賃金</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in minWageNotice.rows" :key="m.id" :class="m.startDate === minWageNotice.from ? 'hit' : ''">
                  <td>{{ slash(m.startDate) }}</td>
                  <td>{{ slash(m.endDate) }}</td>
                  <td class="font-bold">{{ Number(m.minWage).toLocaleString() }}円</td>
                  <td class="text-xs">{{ m.isCurrent ? '適用中' : '予定' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 雇用指示一覧・編集 -->
          <AkbsEmploymentTable ref="employment" :readOnly="isReadOnly" :value="akbs.employment" :original="akbs.originalData" :contractList="val.contractList" :name="akbs.nameStaff" :staffDailyCleaning="akbs.staffDailyCleaning" :staffPref="akbs.pref" :staffCity="akbs.city" :showEnded="showEnded" :showEndedToggle="showEndedToggle" :endedToggleDisabled="isPending" :errors="employmentErrors" @update:showEnded="toggleEnded" :contractNotices="contractNoticeRows" :defaultStartDate="(type === 1 || type === 7) ? (akbs.dateHire || '') : ''" />

          </fieldset>

          <!-- 得意先へ連絡（雇用指示で1件でもチェックが入っているときだけ出す） -->
          <div v-if="contactRows.length" class="mt-4 bg-white border border-indigo-200 rounded shadow">

            <!-- 見出し（クリックで開閉） -->
            <div
              class="flex items-center cursor-pointer select-none text-sm font-bold text-indigo-900 bg-indigo-50 hover:bg-indigo-100 rounded-t px-3 py-2 transition-colors"
              :class="contactOpened ? 'border-b border-indigo-200' : ''"
              @click="contactOpened = !contactOpened"
            >
              <span class="mr-2 text-xs">{{ contactOpened ? '▼' : '▶' }}</span>
              得意先へ連絡
              <span class="ml-3 text-xs font-normal text-gray-500">{{ contactRows.length }}件</span>
            </div>

            <!-- 中身。得意先は物件から入るが、直せるよう入力欄にしている -->
            <div class="accordion px-3" :class="contactOpened ? 'open' : ''">
              <div
                v-for="(row, index) in contactRows"
                :key="'contact' + index"
                class="py-3"
                :class="index ? 'border-t border-dashed border-gray-400' : ''"
              >
                <div class="text-xs font-bold text-blue-700">◆ {{ row.site }}</div>

                <!-- 物件が持っている得意先とは別に、実際の連絡先を持つ。
                     得意先のデータが古いときに送信者が書き換えられるようにするため。
                     書き換えたことが他の人にも分かるよう、元の値を下に出す -->
                <div class="flex flex-wrap items-end gap-x-4 gap-y-3 mt-2">
                  <div class="w-80">
                    <WmsTextInput
                      :name="'contactClient1' + index"
                      caption="得意先名"
                      v-model="row.contactClient1"
                    />
                    <div v-if="row.contactClient1 !== row.client1" class="edited">
                      手動で変更（元：{{ row.client1 || '（なし）' }}）
                    </div>
                  </div>

                  <div class="w-52">
                    <WmsTextInput
                      :name="'contactClient2' + index"
                      caption="支店名"
                      v-model="row.contactClient2"
                    />
                    <div v-if="row.contactClient2 !== row.client2" class="edited">
                      手動で変更（元：{{ row.client2 || '（なし）' }}）
                    </div>
                  </div>

                  <div class="w-40">
                    <WmsTextInput
                      :name="'contactFax' + index"
                      caption="FAX番号"
                      v-model="row.contactFax"
                    />
                    <div v-if="row.contactFax !== row.fax" class="edited">
                      手動で変更（元：{{ row.fax || '（なし）' }}）
                    </div>
                  </div>

                  <PrimaryButton text="メール" size="normal py-1.5" class="w-28" @click="openMessageForClient(true, row)">
                    <Icon slot="before" iconName="Mail" iconType="outline" :strokeWidth="2" class="h-5 w-5" />
                  </PrimaryButton>
                  <PrimaryButton text="FAX" size="normal py-1.5" class="w-28" @click="openMessageForClient(false, row)">
                    <Icon slot="before" iconName="Printer" iconType="outline" :strokeWidth="2" class="h-5 w-5" />
                  </PrimaryButton>
                </div>

                <!-- この物件の送信履歴（送ったかどうかがここで分かる） -->
                <div v-if="contactHistories[index].length" class="mt-2">
                  <div class="text-xs font-bold text-green-700">メール送信履歴</div>
                  <table class="history-table text-xs mt-1">
                    <thead>
                      <tr>
                        <th>種別</th>
                        <th>結果</th>
                        <th>送信者</th>
                        <th>日時</th>
                        <th>得意先</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(h, hi) in contactHistories[index]" :key="'his' + index + '-' + hi">
                        <td class="text-center">{{ h.method }}</td>
                        <td class="text-center">{{ h.sendStatus }}</td>
                        <td class="text-center">{{ h.staff }}</td>
                        <td class="text-center">{{ h.date }}</td>
                        <td>{{ h.client }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

          </div>

          <!-- AKBSに登録した際に確認した休み・代勤・振替（登録済みのときだけ。選んだ操作を見せるだけで、変えられない）
               モーダルを閉じられたときは操作が「何もしない」のまま残る -->
          <div v-if="isRegistered && workingCheckGroups.length" class="mt-4 bg-white border border-indigo-200 rounded shadow">
            <div class="text-sm font-bold text-indigo-900 bg-indigo-50 rounded-t px-3 py-2 border-b border-indigo-200">
              AKBSに登録した際に確認した休み・代勤・振替
              <span class="ml-3 text-xs font-normal text-gray-500">{{ val.workingCheckList.savedName }}</span>
              <!-- 操作した日時・人（もう操作できないことが分かるように。古い控えには無い） -->
              <div v-if="val.workingCheckList.checkedDate" class="text-xs font-normal text-gray-600 mt-1">
                {{ val.workingCheckList.checkedDate }} に{{ val.workingCheckList.checkedUser ? ` ${val.workingCheckList.checkedUser} さんが` : '' }}操作した内容です。
              </div>
            </div>
            <div class="px-3 pb-3">
              <WorkingCheckCard
                v-for="g in workingCheckGroups"
                :key="g.key"
                :list="g.rows"
                :caption="g.caption"
                :radioName="'saved' + g.key"
                :disabled="true"
                :staffName="akbs.nameStaff"
              />
            </div>
          </div>

        </div>
      </div>

      <!-- 休み・代勤・振替の確認モーダル（AKBSへ登録したあと。閉じるボタンは無く、「上記の操作で登録」で閉じる） -->
      <WorkingCheckModal
        v-model="workingCheck"
        :open="workingCheck"
        :val="val"
        :rp="rp"
        :staffName="akbs.nameStaff"
        :typeName="typeName"
        :workingCheckList="val.workingCheckList"
        @saved="savedWorkingCheck"
      />

      <!-- サブタスク（右端のタブで開閉する。入力画面の幅は変えない） -->
      <!-- 承認者（approver=true）は変更箇所を見に来ているので、サブタスクのドロワーは自動で開かない -->
      <AkbsSubTaskPanel ref="subTaskPanel" :value="subTasks" :rp="rp" :autoOpen="!isApprover" :appStatus="val.status || ''" @loading="loading = $event" />

      <!-- 得意先へ連絡モーダル -->
      <MessageForClientModal
        v-model="messageForClient"
        :open="messageForClient"
        :isMail="isMail"
        :rp="rp"
        :akbs="akbs"
        :row="contactRow"
        @sent="getMailHistory"
        @close="messageForClient = false"
      />

    </main>
  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import DropdownButton from '@components/DropdownButton.vue'
import WmsSuggestInput from '@wmscomponents/WmsSuggestInput.vue'
import NumericTextInput2 from '@components/NumericTextInput2.vue'
import PageHeader from '@components/PageHeader.vue'
import WmsTextInput from '@wmscomponents/WmsTextInput.vue'
import WmsDateField from '@wmscomponents/WmsDateField.vue'
import AkbsEmploymentTable, { createEmployment, FARE_ITEMS } from '../components/AkbsEmploymentTable.vue'
import MessageForClientModal from '../components/MessageForClientModal.vue'
import AkbsSubTaskPanel from '../components/AkbsSubTaskPanel.vue'
import WorkingCheckModal from '../components/WorkingCheckModal.vue'
import WorkingCheckCard, { groupByKind } from '../components/WorkingCheckCard.vue'
import * as clientMailManager from '@managers/clientMailManager'
import * as requestPostManager from '@managers/requestPostManager'
import * as taskRequestManager from '@managers/taskRequestManager'
import * as utils from '@libs/utils'
import * as dialogs from '@libs/dialogs'
import { FAMILIY_RELATIONS } from '@libs/constants'
import { WEEK_LIST, PROVISION_SELECTIONS, HOLIDAYTYPE_SELECTIONS, AKBS_STATUS, AKBS_TYPE_NAMES, AKBS_SUB_TASK, isAkbsSubTask, isSameDepartmentForAkbs } from '@libs/constantsAKBS'
import { finishAfterAkbs } from '@libs/akbsMainTask'
import * as utilsAKBS from '@libs/utilsAKBS'
import * as backend from '@libs/backend'
import * as moment from 'moment'

import PageModel from '../../main/pages/PageModel.vue'

// 性別
const SEX_SELECTIONS = [
  { value: '男性', text: '男性' },
  { value: '女性', text: '女性' }
]

// 月
const MONTH_SELECTIONS = [...Array(12)].map((_, i) => ({ value: String(i + 1), text: `${i + 1}月` }))

// 交通費の目安（円）。区分「1日」でこれより高い、区分「1ヶ月」でこれより安いときはNGにする
// キーは FARE_ITEMS の key（電車・バス・駐輪）
const FARE_LIMITS = {
  train: { dailyMax: 3000, monthlyMin: 3000 },
  bus: { dailyMax: 3000, monthlyMin: 3000 },
  parking: { dailyMax: 500, monthlyMin: 1000 },
}

// 退職理由
const RETIRE_REASONS = [
  { text: '自己退職' },
  { text: '病気または怪我' },
  { text: '死亡' },
  { text: '迷惑行為' },
  { text: '遅刻無断欠席常習' },
  { text: '犯罪行為' },
]

const staff = {
  // 清掃員基本情報
  staff: [
    // kanaTo：氏名を日本語入力で打ったとき、変換前のひらがなを拾ってフリガナの欄（半角カナ）に自動で入れる（フリガナが空のときだけ）
    { caption: '氏名', key: 'nameStaff', type: 'text', required: true, kanaTo: 'nameKanaStaff' },
    { caption: 'フリガナ（保存時に半角に変換します）', key: 'nameKanaStaff', type: 'text', class: 'w-64', required: true },
    // star：承認（AKBS登録）のあとも登録済み一覧で直せる項目（項目名の横に赤い ★。帯に説明を出す）
    { caption: '性別', key: 'sex', type: 'select', selections: SEX_SELECTIONS, required: true, star: true },
    { caption: '生年月日', key: 'birthday', type: 'date', required: true, sub: 'birthday', native: true, star: true },
    { caption: '入社日', key: 'dateHire', type: 'date', required: true },
  ],
  // 連絡先
  address: [
    { caption: '〒', key: 'zip', type: 'text', class: 'w-32', required: true, format: 'zip' },
    { caption: '都道府県', key: 'pref', type: 'text', class: 'w-32', required: true },
    { caption: '市区町村', key: 'city', type: 'text', required: true },
    { caption: '町名・番地など', key: 'address', type: 'text', class: 'w-80', required: true },
    { caption: 'マンション名・部屋番号', key: 'address2', type: 'text', class: 'w-80' },
    { caption: 'TEL', key: 'numberPhoneStaff', type: 'tel', class: 'w-40', star: true },
    { caption: '携帯TEL', key: 'numberMobilePhoneStaff', type: 'tel', class: 'w-40', requiredTypes: [1], format: 'mobile', star: true },
  ],
  // 給与
  wage: [
    { caption: 'シルバーパス支給月', key: 'silverMonth', type: 'select', selections: MONTH_SELECTIONS, class: 'w-32' },
    { caption: 'シルバーパス金額', key: 'silverPrice', type: 'number', class: 'w-52', unit: '円' },
    { caption: 'リーダー手当（毎月支給）', key: 'leaderAllowance', type: 'number', class: 'w-52', unit: '円' },
  ],
  // 退職
  retire: [
    { caption: '退職日', key: 'dateResignation', type: 'date', requiredTypes: [6] },
    { caption: '退職理由', key: 'retireReason', type: 'suggest', selections: RETIRE_REASONS, class: 'w-80', requiredKey: 'dateResignation' },
    { caption: '再入社NG', key: 'isBanned', type: 'checkbox' },
    // 入社取消は入社日から1ヶ月以内だけ押せる（ユーザー決定）。既にチェックが入っている申請は、1ヶ月を過ぎていても外せる
    { caption: '入社取消（入社から1ヶ月以内）', key: 'isCancelled', type: 'checkbox', class: 'w-56' },
  ],
  // 備考
  note: [
    { caption: '備考', key: 'noteStaff', type: 'textarea', class: 'w-full' },
    // 変更箇所（mst_staff.changes）は一旦なし。表示も保存もしない。
    // 入れるときは黄色の判定から「項目名：変更前➡変更後」を自動で作り、登録日時ごとに jsonb で積む案（2026-09-09 相談）
  ],
}

// 家族
const family = [
  { caption: '関係', key: 'relationship', type: 'suggest', selections: FAMILIY_RELATIONS, class: 'w-32' },
  { caption: '氏名', key: 'name', type: 'text' },
  { caption: '住所', key: 'address', type: 'text', class: 'w-80' },
  { caption: 'TEL', key: 'phoneNumber', type: 'tel', class: 'w-40' },
]

// 緊急連絡先
const emergency = [
  { caption: '関係', key: 'relationship', type: 'suggest', selections: FAMILIY_RELATIONS, class: 'w-32' },
  { caption: '氏名', key: 'name', type: 'text', kanaTo: 'kana' },
  { caption: 'フリガナ（保存時に半角に変換します）', key: 'kana', type: 'text', class: 'w-64' },
  { caption: '〒', key: 'zip', type: 'text', class: 'w-32', format: 'zip' },
  { caption: '住所', key: 'address', type: 'text', class: 'w-80' },
  { caption: 'TEL', key: 'phoneNumber', type: 'tel', class: 'w-40' },
  // 緊急連絡先の携帯は必須ではない（入っていれば形式だけ見る。2026-09-24）
  { caption: '携帯TEL', key: 'mobile', type: 'tel', class: 'w-40', format: 'mobile' },
]

// 雇用指示は表形式のため AkbsEmploymentTable.vue の createEmployment() で項目を定義している
// （適用開始日／適用終了日／物件／契約コード／作業内容／ゴミ搬出／祝日勤務／全社祝日）

// 入力項目定義（大項目キー → 定義配列）
// required: true は常に必須、requiredTypes: [種別...] はその種別のときだけ必須（種別は getData の type を参照）
// requiredKey: '項目キー' はその項目に入力があるときだけ必須（例：退職日が入っていれば退職理由は必須）
const ITEMS = { ...staff, family, emergency }

// 右上の「一覧」ドロップダウンの項目（value は selectList で見る）
const LIST_MENU = [
  { value: 'akbs', text: 'AKBS申請一覧' },
  { value: 'main', text: 'メインタスク一覧' },
  { value: 'sub', text: 'サブタスク一覧' },
]

// 大項目（アコーディオンの単位）。key は ITEMS のプロパティ名
// repeat: true は追加ボタンで明細を増やせる大項目
const SECTIONS = [
  { key: 'staff', caption: '清掃員' },
  { key: 'address', caption: '連絡先' },
  { key: 'wage', caption: '給与関連' },
  { key: 'retire', caption: '退職' },
  { key: 'family', caption: '家族', repeat: true },
  { key: 'emergency', caption: '緊急連絡先', repeat: true },
  { key: 'note', caption: '清掃員に関する備考' },
]

/**
 * 大項目の開閉状態を作る（初期値はすべて開いた状態）
 */
function createOpened() {
  const opened = {}
  for (const section of SECTIONS) {
    opened[section.key] = true
  }
  return opened
}

/**
 * 大項目1件分の空の入力値オブジェクトを作る
 * checkbox は false、それ以外（select も含む）は空文字を初期値にする
 * @param {String} sectionKey 大項目のキー
 */
function createRow(sectionKey) {
  const row = {}
  for (const item of ITEMS[sectionKey]) {
    if (item.type === 'checkbox') {
      row[item.key] = false
    } else {
      row[item.key] = ''
    }
  }
  return row
}

/**
 * 入力画面の情報（req_akbs の1行）の入れ物を作る
 * 後から足すとリアクティブにならないため、画面で使うキーはここで揃えておく
 * ・interviewsId / employmentId：どのメインタスク（面接・雇い入れ）の申請か
 * ・contractList：物件ごとの契約情報。AKBS登録前は開くたびに取り直し、登録時に req_akbs へ控えて、登録後はその控えを出す
 * ・workingCheckList：AKBS登録時に確認してもらう休み・代勤・振替の一覧と、選んだ操作（{ savedName, list }）。登録後に画面の下へ出す
 */
function createValBase() {
  // returnReason は差戻理由の履歴（文字列。新しい順に「理由＋改行＋ユーザー名 日時」を空行で区切って積む）
  // memo は承認者・総務などに伝えたいことを書く欄（メインタスクの連絡事項とは別。req_akbs.memo）
  return { id: null, interviewsId: null, employmentId: null, contractList: {}, workingCheckList: null, returnReason: '', memo: '' }
}

/**
 * 入力値の入れ物を作る
 * 追加可能な大項目は明細の配列、それ以外は akbs 直下にキーを展開する
 */
function createVal() {
  // previousRetire は再入社のときだけ入る前回の退職の内容（退職の大項目に表示だけする）
  const akbs = { staffDailyCleaning: null, previousRetire: null }
  for (const section of SECTIONS) {
    if (section.repeat) {
      akbs[section.key] = [createRow(section.key)]
    } else {
      Object.assign(akbs, createRow(section.key))
    }
  }

  // 雇用指示（大項目の定義配列ではなく、表形式で持つ）
  // 新規入社の最初の行は物件が増える（新規追加）ので、得意先への連絡は必要な扱いで始める（「物件を追加」と同じ。2026-09-24）
  const employment = createEmployment()
  employment.hasContactClient = true
  akbs.employment = [employment]

  return akbs
}

export default {
  extends: PageModel,
  components: {
    Icon,
    PrimaryButton,
    DropdownButton,
    PageHeader,
    WmsTextInput,
    WmsDateField,
    WmsSuggestInput,
    NumericTextInput2,
    AkbsEmploymentTable,
    MessageForClientModal,
    AkbsSubTaskPanel,
    WorkingCheckModal,
    WorkingCheckCard
  },

  props: {
    // 清掃員名
    name: {
      type: String,
      default: ''
    },
    // 入社日
    dateHire: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      // ローディング
      loading: false,

      // 対象のメインタスク
      rp: {},

      // 入力画面の情報（req_akbs の1行。キーの説明は createValBase を参照）
      val: createValBase(),

      // AKBSに関する情報
      akbs: createVal(),

      // 大項目
      sections: SECTIONS,

      // 右上の「一覧」ドロップダウンの項目
      listMenu: LIST_MENU,

      // 大項目の開閉状態
      opened: createOpened(),

      // 得意先へ連絡の開閉状態
      contactOpened: true,

      // 保存時の矛盾チェックでNGになった欄（'大項目.明細の位置.項目キー'。雇用指示は 'employment.行の位置.キー'）
      // 次に保存するまで残し、赤い枠でどこがNGか示す
      errorKeys: [],
      // 最低賃金を下回ったときに雇用指示一覧の上に出す、その行の営業所の最低賃金マスタ（{ pref: 営業所, rows, name, from, minWage }。無ければ出さない）
      minWageNotice: null,
      // 確認ダイアログの「このまま○○してよろしいですか？」に入れる言葉（申請／登録）
      confirmWord: '申請',
      // 入力チェックでエラーのダイアログを出したか（確認ダイアログのキャンセルと区別する。承認者には「この画面で直せません」を足す）
      validateError: false,

      // 得意先へ連絡モーダル
      messageForClient: false,
      // メールかFAXか
      isMail: false,
      // モーダルを開いた雇用指示
      contactRow: {},

      // 得意先へのメール送信履歴（この申請の分）
      mailHistory: [],

      // 契約詳細の申請のお知らせ（この清掃員が働いている契約の終了・変更。契約詳細入力画面の申請から取る）
      contractNotices: [],
      // 承認待ちになった後に、この申請に書かれた契約詳細の変更内容（req_akbs.contract_notes。画面には出さない）の中身が変わったか（承認者の登録時に確認を出す）
      notesAfterApply: false,

      // 終わった雇用指示も表に出しているか（表のチェック。保存しない。足した行は触らなければ保存・登録で書かれない）
      showEnded: false,

      // 休み・代勤・振替の確認モーダル（AKBSへ登録したあと、確認するものがあるときだけ開く）
      workingCheck: false,

      // サブタスク（AKBSとは関係ないただのTODO。取得だけここで行い、編集・保存は右端のパネルの中で完結する）
      subTasks: [],

      // メインタスクID
      mainId: null,

      // 面接・雇い入れどちらから遷移か
      from: null,

      // このデータのpostgresのID
      id: null,

      // 承認待ち一覧の「確認」から開いたか（URL の approver=true。承認者向けの画面になり、申請取消は出さない）
      isApprover: false,

      // 削除した申請で、メインタスクが完了している（復活できない。閲覧のみ。バックの getData が返す）
      deletedFinal: false,

      // 今回の差戻理由（差戻ボタンを押したときに val.returnReason の先頭へ積む）
      newReturnReason: '',

      // 種別
      type: null,
      typeName: null
    }
  },

  async created() {
    // フリガナの自動入力の途中の状態（行ごと。画面に出さないのでリアクティブにしない。row に先頭 _ のキーを足すとバックで化けるため別に持つ）
    this.kanaStates = new WeakMap()
    this.mainId = this.$route.query.requestPostId
    this.from = this.$route.query.from
    this.id = this.$route.query.id
    this.isApprover = this.$route.query.approver === 'true'
    // 最初の1回だけサブタスクとメール送信履歴も受け取る（保存後の取り直しでは取り直さない）
    await this.getData(true)

    // 承認者として開いたが、もう AKBS に登録済み（サブタスク「雇用指示承認」のボタンは承認のあとも押せる）：知らせて、そのまま見るだけにする
    if (this.isApprover && this.val.id && this.isRegistered) {
      await dialogs.showInfoDialog('すでに承認済みです', 'この申請は AKBS へ登録済みです。内容の確認だけできます。', 'OK')
    } else if (this.isApprover && this.val.id && !this.isPending) {
      // 承認待ち一覧の「確認」から開いたのに承認待ちでも登録済みでもなければ、一覧を見てから開くまでの間に申請者が取り消している
      // （取得に失敗したときは val.id が無いので、ここでは戻さない）
      await dialogs.showWarningDialog('申請が取り消されています', '先ほど申請が取り消されました。申請一覧に戻ります。')
      this.toList()
      return
    }

    // 承認者は変わったところをすぐ見たいので、変更のある大項目だけ開いた状態にする
    // 保存済みの申請（一時保存・差戻・承認待ち・AKBS登録済み）を開き直したときも同じ。続きを入力する人・総務が見たいのも変わったところなので
    // ・未保存（空の枠を含む。val.id が無い）：まだ何も変えていないので、全部開いたまま
    // ・新規入社・再入社：全部が新しい内容なので、全部開いたまま
    // 開いたときの1回だけ（保存後の取り直しでは開閉を変えない）
    const isHire = this.type === 1 || this.type === 7
    if (this.isApprover || (this.val.id && !isHire)) {
      this.openChangedOnly()
    }
  },

  /**
   * 別の画面へ移る前に、サブタスクに保存していない変更があれば確認する（確認はパネルの中で行う）
   */
  async beforeRouteLeave(to, from, next) {
    const panel = this.$refs.subTaskPanel
    next(!panel || await panel.confirmLeave())
  },

  computed: {
    /**
     * 承認待ちか（内容は閲覧のみ。申請取消で一時保存に戻すと直せる）
     */
    isPending() {
      return this.val.status === AKBS_STATUS.PENDING
    },

    /**
     * AKBSへ登録済みか（本番に書き込み済みなので、申請・一時保存・AKBSへ登録・差戻はさせない）
     */
    isRegistered() {
      return this.val.status === AKBS_STATUS.REGISTERED
    },

    /**
     * AKBS登録時に確認した休み・代勤・振替を種類ごとに分けたもの（登録済みの画面の下に出す。無ければ空）
     */
    workingCheckGroups() {
      const wc = this.val.workingCheckList
      return groupByKind(wc && wc.list)
    },

    /**
     * 閲覧のみか（承認待ち・AKBS登録済み・復活できない削除済み）
     */
    isReadOnly() {
      return this.isPending || this.isRegistered || this.deletedFinal
    },

    /**
     * メインタスクの連絡事項を、URL とそれ以外に分けたもの（URL を押せるリンクにするため）
     */
    remarkParts() {
      const text = String((this.rp && this.rp.employmentRemarks) || '')
      return text.split(/(https?:\/\/[^\s]+)/).filter(t => t).map(t => ({ text: t, url: /^https?:\/\//.test(t) }))
    },

    /**
     * メモに何か書いてあるか（空白だけは無しと見る。書いてあれば目立たせる）
     */
    hasMemo() {
      return !!String(this.val.memo || '').trim()
    },

    /**
     * 契約詳細の申請のお知らせに、この申請の雇用指示の様子を添えたもの（画面の上の表用）
     * 契約終了のときだけ、その契約の雇用指示に終了日が入っているかを見る
     * ・todo：終了日が空、または契約終了日より後の雇用指示がある（作業終了が必要）
     * ・ok  ：全部、契約終了日までに終わる
     * ・none：この契約の雇用指示が画面に無い
     */
    contractNoticeRows() {
      const day = d => String(d || '').slice(0, 10).replace(/\//g, '-')
      return this.contractNotices.map(n => {
        let state = ''
        if (n.mode === 'end') {
          const rows = (this.akbs.employment || []).filter(e => String(e.cdSite) === n.siteCode && String(e.cdContract) === n.cdContract && !e.isChangedFrom)
          if (!rows.length) {
            state = 'none'
          } else {
            state = rows.some(e => !e.endDateWork || (n.endDate && day(e.endDateWork) > day(n.endDate))) ? 'todo' : 'ok'
          }
        }
        return { ...n, state }
      })
    },

    /**
     * 作業終了がまだの契約（契約終了の申請があるのに、雇用指示が終わっていない）
     */
    contractNoticeTodos() {
      return this.contractNoticeRows.filter(n => n.state === 'todo')
    },

    /**
     * 承認の操作（差戻・AKBSへ登録）ができるか
     * AKBS申請一覧の「確認」から開いていて（approver=true）、かつログインユーザーにAKBS権限（akbsAuth）があるとき
     */
    canApprove() {
      const auth = this.$store.getters.auth
      return this.isApprover && !!(auth && auth.akbsAuth) && !this.isMyRequest
    },

    /**
     * 自分が作成した申請か（作成者のユーザーID creatorId とログインユーザーの id を文字で比べる）
     * 作成者本人は承認（差戻・AKBSへ登録）できない。申請一覧の「確認」は出ないが、URL を直接開いたときのためにここでも止める（ユーザー指示）
     */
    isMyRequest() {
      const me = this.$store.getters.user && this.$store.getters.user.id
      return !!this.val.creatorId && !!me && String(this.val.creatorId) === String(me)
    },

    /**
     * 今回の差戻理由を書けるか（承認の操作ができて、承認待ちのとき）
     */
    canWriteReturnReason() {
      return this.canApprove && this.isPending
    },


    /**
     * 雇用指示の表に「から適用している勤務を表示」の基準日を出すか
     * 承認待ちと、過去の勤務が無い入社・再入社では出さない
     */
    showEndedToggle() {
      return this.type !== 1 && this.type !== 7
    },

    /**
     * 得意先へ連絡する雇用指示（表でチェックが入っているもの）
     */
    contactRows() {
      return (this.akbs.employment || []).filter(row => row.hasContactClient)
    },

    /**
     * 得意先へ連絡の行ごとの送信履歴（contactRows と同じ並び）
     */
    contactHistories() {
      return this.contactRows.map(row => this.historyOf(row))
    },

    /**
     * 大項目ごとの、取得時から変わった項目の数（大項目キー → 件数）
     */
    changedCounts() {
      const counts = {}
      for (const section of SECTIONS) {
        counts[section.key] = 0
        if (!this.akbs.originalData) {
          continue
        }
        const rows = this.rows(section)
        for (let i = 0; i < rows.length; i++) {
          for (const item of this.items[section.key]) {
            if (this.isChanged(section, item, i)) {
              counts[section.key]++
            }
          }
        }
      }
      return counts
    },

    /**
     * 雇用指示の表に渡すNGの欄（'行の位置.キー' の形にして渡す）
     */
    employmentErrors() {
      return this.errorKeys
        .filter(key => key.startsWith('employment.'))
        .map(key => key.replace('employment.', ''))
    },

    /**
     * AKBS項目（大項目キー → 入力項目定義）
     * requiredTypes を持つ項目は、現在の種別（type）が含まれるときだけ required にする
     */
    items() {
      const items = utils.clone(ITEMS)
      for (const key of Object.keys(items)) {
        for (const item of items[key]) {
          if (item.requiredTypes) {
            item.required = item.requiredTypes.includes(this.type)
          }
        }
      }
      return items
    },

    /**
     * 今日（生年月日に未来日を入れさせないための上限）
     */
    today() {
      return utils.getToday()
    },

    /**
     * ログインユーザー名（フリガナ無し）
     */
    user() {
      return utils.deleteKana(this.$store.getters.user.user_name)
    },
  },

  methods: {
    /**
     * 日付欄を変えたとき（退職日なら雇用指示の終了日を揃える）
     * WmsDateField の change は利用者が変えたときだけ飛ぶので、開いただけでは動かない
     * @param {Object} item 項目
     */
    async dateFieldChanged(item) {
      if (item.key !== 'dateResignation') {
        return
      }
      // v-model の反映を待ってから読む
      await this.$nextTick()
      await this.fillEndDatesByResignation(this.akbs.dateResignation)
    },

    /**
     * 退職日が入ったら、まだ終わっていない勤務の終了日を退職日にする
     * 「終わっていない」かどうかは取得時の元データ（originalData）の終了日で判断する
     * （画面で退職日を直したときも追いかけて直せて、元から作業終了が入っていた行は触らない）
     * 手で終了日を打った行も退職日で上書きする（退職日の方を優先する。2026-09-11 確認）
     * 変更前の行（isChangedFrom）は変更後の開始日から自動で決まるので触らない
     * 利用者が退職日を変えたときだけ呼ぶ。開いただけで動くと、退職日と別の日で保存した終了日が戻ってしまう
     * @param {String} date 退職日
     */
    async fillEndDatesByResignation(date) {
      if (!date) {
        return
      }

      // 自動で入れた行の数（1行も無ければ知らせない）
      let count = 0

      for (const row of this.akbs.employment || []) {
        // 取り消した行は無かった扱いなので触らない
        if (row.isChangedFrom || row.isCancelled || row.endDateWork === date) {
          continue
        }

        // 元データに終了日があれば、もう終わっている勤務なので触らない
        const orig = this.originalEmployment(row)
        if (orig && orig.endDateWork) {
          continue
        }

        row.endDateWork = date
        // 作業終了ボタンを押したのと同じ状態にする（AKBS登録済みの行のみ。得意先への連絡も要るのでチェックを入れる）
        if (row.sequenceNumber && row.mode === 'locked') {
          row.mode = 'ended'
          row.hasContactClient = true
        }
        count++
      }

      if (!count) {
        return
      }

      // 勝手に変わったことに気づけるよう、知らせてから雇用指示まで動かす
      await dialogs.showInfoDialog('雇用指示の終了日を入力しました', '雇用指示で「終了日」が入っていない行に退職日と同じ日を入力しました。\nまた、得意先へ連絡にもチェックを入れています。不要な場合はチェックを外してください。')
      if (this.$refs.employment && this.$refs.employment.$el) {
        this.$refs.employment.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },

    /**
     * 予定データ取得
     * @param {Boolean} isFirst 画面を開いたときの取得なら true（サブタスクとメール送信履歴も受け取る。保存後の取り直しでは受け取らない）
     */
    async getData(isFirst) {
      this.loading = true
      let val = null
      let akbs = null
      // 再入社で未保存なら、取得のあとに「以前の情報を初期値にしている」と知らせる
      let noticeRehire = false
      // 申請のあとに AKBS 側で変わっていて、最新に差し替えた行（取得のあとに知らせる）
      let latestNotices = []
      // 退職の雇い入れで、メインタスクの退職日を初期値にしたか（取得のあとに雇用指示の終了日へ写す）
      let fillRetirement = false
      try {
        // メインタスクと保存しているデータを取得
        const res = await backend.searchData('akbsInputForm/getData', { mainId: this.mainId, from: this.from, id: this.id })

        // メインタスクは必ず存在する
        this.rp = res.data.data.rp
        // 削除した申請で、メインタスクが完了している（復活できない）
        this.deletedFinal = !!res.data.data.deletedFinal

        // 画面を開いたときだけ受け取るもの（保存や状況変更では変わらないため、取り直しでは通信しない）
        if (isFirst) {
          // サブタスク（右端のパネルへ渡す。AKBSとは独立して保存する）
          this.subTasks = utils.clone(res.data.data.subTasks || [])

          // 得意先へのメール送信履歴（「得意先へ連絡」で物件ごとに出す。送信後はモーダルの sent で取り直す）
          await this.getMailHistory()
        }

        if (this.rp.category[0].includes('面接')) {
          this.type = 1
        } else {
          switch (this.rp.employmentClass[0]) {
            case '新規入社':
              this.type = 1
              break;

            case '物件追加':
              this.type = 2
              break;

            case '契約変更':
              if (this.rp.modifyType == 1) {
                if (this.rp.changeOnlyPrice) {
                  this.type = 4
                } else {
                  this.type = 3
                }
              } else {
                this.type = 5
              }
              break;

            case '退職':
              this.type = 6
              break;

            case '再入社':
              this.type = 7
              break;

            default:
              this.type = 100
              break;
          }
        }

        this.typeName = AKBS_TYPE_NAMES[this.type]

        // AKBS情報（一時保存・申請済みなら req_akbs のもの、未保存でも新規入社以外はAKBSの現在値が返る）
        if (res.data.data.val) {
          val = utils.clone(res.data.data.val)
          akbs = val.akbs
          delete val.akbs
        }

        // 勤務形態はコードの配列（['00'] 等）で持っているため、画面表示用の選択肢に直す
        if (akbs) {
          utilsAKBS.setAttendance(akbs.employment)
        }

        // 一時保存・申請済みかどうか（req_akbs に行があれば id が付いている）
        // 一度も保存していないときの決めごとはここにまとめる。保存済みなら req_akbs の値をそのまま使う
        const saved = !!(val && val.id)
        if (!saved) {
          val = val || {}

          // どのメインタスクから開いたか（メインタスクボタンと保存時の req_akbs の紐づけに使う。保存済みなら req_akbs の値がそのまま入る）
          if (this.from === 'interviews') {
            val.interviewsId = this.mainId
          } else if (this.from === 'employment') {
            val.employmentId = this.mainId
          }

          if (this.type === 1 || this.type === 7) {
            // 再入社は以前在職していたときの情報が AKBS から返るので、それを初期値にしていることを画面が出たあとで知らせる
            // 退職の項目（退職日・退職理由・再入社NG・入社取り消し）は前回の退職のものなので空にする
            // 承認者が前回の退職を見られるよう、空にする前の値を previousRetire に控える（akbs に載るので保存後も残る）
            noticeRehire = this.type === 7 && !!akbs
            if (noticeRehire) {
              akbs.previousRetire = {}
              for (const item of ITEMS.retire) {
                akbs.previousRetire[item.key] = akbs[item.key]
              }
              Object.assign(akbs, createRow('retire'))
            }

            // 入社は AKBS にまだ無いので空の入れ物に、面接・雇い入れから渡された清掃員名・入社日を入れる
            akbs = akbs || createVal()
            // 申請一覧から開いたとき（未保存の枠）は渡されないので、メインタスクの値を使う
            akbs.nameStaff = this.name || this.rp.employmentName || this.rp.interviewsName || ''
            akbs.dateHire = this.dateHire || this.rp.employmentHireDate || this.rp.interviewsHireDate || ''
            // 最初から出ている雇用指示の行（物件未選択・未保存）の開始日は入社日にする（「物件を追加」で足した行と同じ。ユーザー指示）
            for (const row of akbs.employment || []) {
              if (!row.sequenceNumber && !row.cdSite && !row.startDateWork && akbs.dateHire) {
                row.startDateWork = akbs.dateHire
              }
            }
          } else if (akbs) {
            // 入社以外は AKBS の現在値が元データ（変更箇所を黄色にするため）。保存済みなら元データも一緒に保存されている
            akbs.originalData = utils.clone(akbs)

            // 退職の雇い入れで、まだ退職日が無ければ、メインタスクに入れた退職日を初期値にする（元データには入れないので黄色になる）
            // 雇用指示の終了日は、画面が出たあとに退職日から入れる（下の fillRetirementFromTask）
            if (this.type === 6 && !akbs.dateResignation && this.rp.employmentRetirementDate) {
              akbs.dateResignation = String(this.rp.employmentRetirementDate).slice(0, 10)
              fillRetirement = true
            }
          }
        }

        // AKBS登録済みなら、登録した時点の契約情報の控えを出す。それ以外は空にして下で取り直す
        const registered = val.status === AKBS_STATUS.REGISTERED
        const contractList = (registered && val.contractList) || {}

        // 保存済みで登録前なら、AKBS の今の値で「画面で触っていないところ」を最新にする
        // 賃金は賃金一括変更でも変わるため、申請したときの控えのまま登録すると、その後の変更を消してしまう
        if (saved && !registered && akbs && akbs.originalData && res.data.data.live) {
          const live = utils.clone(res.data.data.live)
          utilsAKBS.setAttendance(live.employment)
          latestNotices = this.mergeLatest(akbs, live)
        }

        // 後から足すとリアクティブにならないため、読み込み時にキーを揃えておく
        this.val = { ...createValBase(), ...val, contractList }
        // メモは未入力だと null で返るので、入力欄用に空文字へ揃える
        this.val.memo = this.val.memo || ''
        if (akbs) this.akbs = akbs

        // この清掃員が働いている契約に、契約詳細の申請（契約終了など）があれば知らせる
        await this.loadContractNotices()

        // AKBS登録前は、今回いじる雇用指示（新規追加・上書き・契約内容変更・賃金のみ変更）の物件の契約情報を今の値で取り直す（矛盾チェックを最新の数字で行うため）
        // 取れなかった物件は表の中で知らせる
        if (!registered) {
          this.$nextTick(() => {
            if (this.$refs.employment) {
              this.$refs.employment.loadSiteInfo()
            }
          })
        }

      } catch (e) {
        console.log(e)
        await this.errorDialog('データ取得エラー', 'データの取得に失敗しました。画面を再読み込みしてください。')
      } finally {
        this.loading = false
      }

      // 再入社で未保存のときの知らせ（ローディングを消してから出す）
      if (noticeRehire) {
        await dialogs.showInfoDialog('再入社', '以前在職していた時の情報を初期値で表示します。\n内容を確認して、変わっているところは直してください。')
      }

      // 退職の雇い入れで、メインタスクの退職日を初期値にしたとき：退職日を手で入れたのと同じく、終わっていない雇用指示の終了日を退職日にする
      // （作業終了にして得意先へ連絡にチェック。知らせのダイアログも同じ）
      if (fillRetirement) {
        await this.fillEndDatesByResignation(this.akbs.dateResignation)
      }

      // 申請のあとに AKBS 側で賃金・終了日が変わっていた行の知らせ（賃金を変えていない行だけ差し替えている）
      if (latestNotices.length) {
        await dialogs.showInfoDialog('AKBSの内容を最新にしました', `申請のあとに本番のAKBSで変わっていた行があるので、最新の内容にしました。\n${latestNotices.map(n => `・${n}`).join('\n')}`)
      }
    },

    /**
     * 「終了した雇用指示も表示」のチェックを変えた
     * ON：AKBS から終わった雇用指示を全部（賃金付き）取り、表に無い行を足す（元データにも足して、黄色の判定・登録時の突き合わせが効くようにする）
     *     足した行はロック中の終了行（灰色）。「修正（上書き）」で終了日を消す・直すと、承認で AKBS の終了日が書き換わる（退職の取り消し・延期、間違えた作業終了の直し）
     * OFF：足した行のうち触っていないものを表から取り除く（触った行は残す）
     * @param {Boolean} value チェックの状態
     */
    async toggleEnded(value) {
      this.showEnded = value
      const akbs = this.akbs
      const rows = akbs.employment || []
      const today = utils.getToday()

      if (!value) {
        const table = this.$refs.employment
        akbs.employment = rows.filter(row => {
          const orig = this.originalEmployment(row)
          const ended = !!(orig && orig.endDateWork && orig.endDateWork < today)
          return !ended || !table || !table.isUntouched(row)
        })
        return
      }

      if (!akbs.staffDailyCleaning) {
        return
      }
      this.loading = true
      try {
        // API: akbsInputForm/getEndedEmployments
        const res = await backend.searchData('akbsInputForm/getEndedEmployments', { staffDailyCleaning: akbs.staffDailyCleaning })
        const list = (res.data && res.data.data) || []
        utilsAKBS.setAttendance(list)

        if (!akbs.originalData) {
          akbs.originalData = { employment: [] }
        }
        if (!akbs.originalData.employment) {
          akbs.originalData.employment = []
        }

        for (const e of list) {
          if (rows.some(r => r.sequenceNumber == e.sequenceNumber)) {
            continue
          }
          const row = utils.clone(e)
          if (!row.wages) {
            row.wages = []
          }
          rows.push(row)
          if (!akbs.originalData.employment.some(o => o.sequenceNumber == e.sequenceNumber)) {
            akbs.originalData.employment.push(utils.clone(row))
          }
        }
        akbs.employment = rows

        if (!list.length) {
          await dialogs.showInfoDialog('終了した雇用指示はありません', 'この清掃員に終わった雇用指示はありません。')
        }
      } finally {
        this.loading = false
      }
    },

    /**
     * 契約詳細の申請のお知らせを取る
     * （この清掃員が働いている契約の終了・変更と、承認待ちの後に、この申請に書かれた契約詳細の変更内容の中身が変わったか）
     * API: akbsContractForm/getStaffNotices
     * 雇い入れのメインタスクで、清掃員コードがあるときだけ
     * AKBS登録済みの申請は、バックがお知らせを返さない（終わった申請）
     * バックは変更内容のメモ（notes）も返すが、行ごとの変更点はお知らせ（notices）の details に添えてあるので、メモそのものは使わない
     * 取れなくても画面は使えるので、エラーにはしない（前に取れた内容をそのまま残す）
     */
    async loadContractNotices() {
      const employmentId = this.val.employmentId || (this.from === 'employment' ? this.mainId : '')
      const staffCode = this.akbs.staffDailyCleaning
      if (!employmentId || !staffCode) {
        this.contractNotices = []
        this.notesAfterApply = false
        return
      }
      try {
        const res = await backend.postData('akbsContractForm/getStaffNotices', { employmentId, staffCode: String(staffCode) })
        const data = (res.data && res.data.data) || {}
        this.contractNotices = data.notices || []
        this.notesAfterApply = !!data.notesAfterApply
      } catch (e) {
        console.log(e)
      }
    },

    /**
     * 契約詳細入力画面を別タブで開く
     */
    openContractForm(notice) {
      const resolvedRoute = this.$router.resolve({ name: 'AkbsContractForm', query: { id: notice.formId } })
      window.open(resolvedRoute.href, '_blank')
    },

    /**
     * AKBS の今の値で、この申請で賃金を変えていないシーケンスだけを最新にする
     * ・触っていない行（changed も isChangedFrom も無い）：行ごと今の値に差し替える
     * ・触っているが賃金は変えていない行（勤務時間の上書きなど）：賃金を今の値に差し替え、終了日は AKBS 側で入った・早まったなら採る
     * ・賃金を変えた行（賃金のみ変更・上書きで賃金を直した・足した行）：何も差し替えない。代わりに保存のたびに最低賃金と比べる（validate）
     *   一括変更で入った賃金を勝手に混ぜると、その前に賃金を入れたいときなどに編集しづらく、何が変わったか分からなくなるため
     * ・元データ（黄色の判定に使う originalData）は、差し替えた行は今の値、賃金を変えた行は申請時のまま
     * ・足した行（採番なし）と、AKBS で終わって取れなくなった行はそのまま
     * changed は保存のたびに markChanged で立て直しているので、保存済みの控えでは「触った行」の目印として使える
     * @param {Object} akbs 画面の内容（req_akbs の控え）
     * @param {Object} live AKBS の今の値（getStaffAkbsData。勤務形態は選択肢に直してから渡す）
     * @returns 変わっていた行の知らせ（物件名と何が変わったか）
     */
    mergeLatest(akbs, live) {
      const notices = []
      const liveRows = {}
      for (const e of live.employment || []) {
        if (e.sequenceNumber) {
          liveRows[e.sequenceNumber] = e
        }
      }
      const oldOrigRows = {}
      for (const e of akbs.originalData.employment || []) {
        if (e.sequenceNumber) {
          oldOrigRows[e.sequenceNumber] = e
        }
      }

      // 突き合わせ用（適用日・時給。交通費は一括変更では変わらないので見ない）
      const wageText = w => `${w.startDate || ''}|${w.basicWage === null || w.basicWage === undefined || w.basicWage === '' ? '' : Number(w.basicWage)}`
      const wagesText = wages => (wages || []).filter(w => w.id).map(wageText).join(';')
      const siteName = row => String(row.site || row.cdSite || '').replace(/\n/g, ' ')

      // 賃金を変えたシーケンス（元データは申請時のまま残す）
      const wageChangedSeqs = new Set()

      const rows = akbs.employment || []
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        if (!row.sequenceNumber) {
          continue
        }
        const liveRow = liveRows[row.sequenceNumber]
        if (!liveRow) {
          continue
        }

        // この申請で取り消した行は触らない（差し替えると取消の印が消える）
        if (row.isCancelled) {
          continue
        }

        // この申請で賃金を変えた行は触らない
        if (this.wagesChanged(row, oldOrigRows[row.sequenceNumber])) {
          wageChangedSeqs.add(row.sequenceNumber)
          continue
        }

        // AKBS 側で変わったかは「申請したときの元データ」と「AKBS の今の値」で比べる
        // （画面の値と比べると、この申請で入れた終了日などが「AKBS 側で変わった」と誤って出る）
        const origRow = oldOrigRows[row.sequenceNumber] || row
        const wageChanged = wagesText(origRow.wages) !== wagesText(liveRow.wages)
        const endChanged = (origRow.endDateWork || '') !== (liveRow.endDateWork || '')

        // 触っていない行は行ごと今の値に
        if (!row.changed && !row.isChangedFrom) {
          if (wageChanged || endChanged) {
            notices.push(`${siteName(row)}（${[wageChanged ? '賃金' : '', endChanged ? '終了日' : ''].filter(x => x).join('・')}）`)
          }
          const copy = utils.clone(liveRow)
          // 賃金が無い行は null で返るので、表が回せるよう空の配列にしておく
          if (!copy.wages) {
            copy.wages = []
          }
          rows.splice(i, 1, copy)
          continue
        }

        // 触っているが賃金は変えていない行：賃金を今の値に
        if (wageChanged) {
          row.wages = (liveRow.wages || []).map(w => utils.clone(w))
          notices.push(`${siteName(row)}（賃金）`)
        }

        // 終了日は AKBS 側で入った・早まったなら採る（契約の終了などで入る）
        if (liveRow.endDateWork && (!row.endDateWork || liveRow.endDateWork < row.endDateWork)) {
          row.endDateWork = liveRow.endDateWork
          notices.push(`${siteName(row)}（終了日）`)
        }
      }

      // 元データの雇用指示：差し替えた行は今の値、賃金を変えた行は申請時のまま（黄色の判定と登録時の突き合わせに使う）
      // AKBS で終わって今の値に無くなった行の元データはそのまま残す
      const newOrig = (live.employment || []).map(e => (wageChangedSeqs.has(e.sequenceNumber) && oldOrigRows[e.sequenceNumber]) ? oldOrigRows[e.sequenceNumber] : utils.clone(e))
      for (const e of akbs.originalData.employment || []) {
        if (e.sequenceNumber && !liveRows[e.sequenceNumber]) {
          newOrig.push(e)
        }
      }
      akbs.originalData.employment = newOrig
      return notices
    },

    /**
     * この申請で、その行の賃金を変えたか（足した・直した・消した）
     * 元データの賃金と、件数・id・開始日・終了日・時給・交通費を突き合わせる。id の無い賃金があれば足したもの
     * @param {Object} row 雇用指示の行
     * @param {Object} orig 元データの行（無ければ足した行なので「変えた」扱い）
     */
    wagesChanged(row, orig) {
      if (!orig) {
        return true
      }
      const wages = row.wages || []
      const origWages = orig.wages || []
      if (wages.length !== origWages.length || wages.some(w => !w.id)) {
        return true
      }
      const feeKeys = FARE_ITEMS.map(f => f.feeKey)
      const keys = ['startDate', 'basicWage', ...FARE_ITEMS.map(f => f.typeKey), ...feeKeys]
      // 数値は '0' と 0 を同じに見る
      const text = v => (v === null || v === undefined || v === '') ? '' : (isNaN(Number(v)) ? String(v) : String(Number(v)))
      // 交通費の金額は AKBS の NULL と画面で入れた 0（fillZeroFare）を同じに見る。違うと触っていない行が「賃金を変えた行」になり、
      // 開いたときに差し替わらず、登録の突き合わせでも stale になる
      const value = (k, v) => (feeKeys.includes(k) && text(v) === '') ? '0' : text(v)
      return wages.some((w, wi) => {
        const o = origWages[wi] || {}
        if (String(w.id) !== String(o.id)) {
          return true
        }
        return keys.some(k => value(k, w[k]) !== value(k, o[k]))
      })
    },

    /**
     * AKBS 登録の前に突き合わせる、AKBS の値（今回書く行だけ）
     * 元データ（originalData）から取る。賃金を変えていない行は開いたときの今の値、賃金を変えた行は申請時の値（stale）
     * 賃金を変えた行が申請のあとに本番で変わっていれば、登録すると本番の変更を消してしまうので、バックが止める
     * @returns [{ sequenceNumber, endDateWork, stale, wages: [{ id, startDate, basicWage }] }]
     */
    expectedAkbs() {
      const map = {}
      for (const e of (this.akbs.originalData && this.akbs.originalData.employment) || []) {
        if (e.sequenceNumber) {
          map[e.sequenceNumber] = e
        }
      }

      const list = []
      for (const row of this.akbs.employment || []) {
        // 取り消す行は本体から消すだけなので、賃金・終了日の突き合わせはしない
        const orig = row.sequenceNumber && row.changed && !row.isCancelled ? map[row.sequenceNumber] : null
        if (!orig) {
          continue
        }
        list.push({
          sequenceNumber: row.sequenceNumber,
          endDateWork: orig.endDateWork || '',
          stale: this.wagesChanged(row, orig),
          wages: (orig.wages || []).filter(w => w.id).map(w => ({ id: w.id, startDate: w.startDate || '', basicWage: w.basicWage }))
        })
      }
      return list
    },

    /**
     * 大項目の開閉
     * @param {Object} section 大項目（SECTIONS の1要素）
     */
    toggle(section) {
      this.opened[section.key] = !this.opened[section.key]
    },

    /**
     * 大項目を全て開く（雇用指示一覧・編集、得意先へ連絡も含む）
     * サブタスクのドロワーは入力画面に重なるので開かない
     */
    allOpen() {
      for (const section of SECTIONS) {
        this.opened[section.key] = true
      }
      this.contactOpened = true
      if (this.$refs.employment) {
        this.$refs.employment.setOpened(true)
      }
    },

    /**
     * 取得時から変わったところがある大項目だけ開き、それ以外は閉じる（承認者が開いたときと、保存済みの申請を開き直したとき用。created）
     * 家族・緊急連絡先は明細の増減も変更として見る（isSectionChanged）。それ以外は見出しの変更件数と同じ（changedCounts）
     * 雇用指示の表は変更・追加のどちらかがあれば開く。得意先へ連絡はそのまま
     * 契約終了の申請があるのに作業終了がまだの雇用指示があるときも、表を開く（上の枠で「下の雇用指示で作業終了にしてください」と案内しているため）
     * 元データが無い（新規入社など比べるものが無い）ときは全部が新しい内容なので、何もせず全て開いたままにする
     */
    openChangedOnly() {
      if (!this.akbs.originalData) {
        return
      }

      for (const section of SECTIONS) {
        this.opened[section.key] = section.repeat ? this.isSectionChanged(section) : this.changedCounts[section.key] > 0
      }

      // 雇用指示の表は描画が終わってから（ref が付くのを待つ）
      this.$nextTick(() => {
        if (this.$refs.employment) {
          this.$refs.employment.setOpened(this.$refs.employment.hasChanged || this.contractNoticeTodos.length > 0)
        }
      })
    },

    /**
     * 大項目を全て閉じる（雇用指示一覧・編集、得意先へ連絡、サブタスクのドロワーも含む）
     */
    allClose() {
      for (const section of SECTIONS) {
        this.opened[section.key] = false
      }
      this.contactOpened = false
      if (this.$refs.employment) {
        this.$refs.employment.setOpened(false)
      }
      if (this.$refs.subTaskPanel) {
        this.$refs.subTaskPanel.close()
      }
    },

    /**
     * メインタスク画面を別タブで開く
     * 開くのは雇い入れ（employmentId）。無ければ面接（interviewsId）
     * （一度も保存していないときは getData で from に従って入れている）
     */
    toMainTask() {
      const route = this.mainTaskRoute()
      if (!route) {
        return
      }
      const resolvedRoute = this.$router.resolve(route)
      window.open(resolvedRoute.href, '_blank')
    },

    /**
     * メインタスク画面の遷移先（雇い入れ employmentId。無ければ面接 interviewsId。どちらも無ければ null）
     * 別タブで開く toMainTask と、申請後に同じタブで移るときの両方で使う
     */
    mainTaskRoute() {
      const requestPostId = this.val.employmentId || this.val.interviewsId
      if (!requestPostId) {
        return null
      }
      return {
        name: 'RequestPost',
        query: {
          mode: 1,
          requestPostId
        }
      }
    },

    /**
     * 一時保存・申請
     * 申請は必須項目がそろっていないと保存しない。一時保存は未入力でも保存する
     * @param {Boolean} isApply 申請なら true、一時保存なら false
     */
    async save(isApply) {
      // 二度押しを防ぐため、押した直後にマスクを掛ける（必須・矛盾チェックの中に最低賃金マスタの取得の待ちがあり、その間にもう一度押せたため）
      if (this.loading) return
      this.loading = true
      try {
        await this.saveBody(isApply)
      } finally {
        this.loading = false
      }
    },

    /**
     * 一時保存・申請の中身（save から呼ぶ。マスクは save が掛ける）
     * @param {Boolean} isApply 申請なら true、一時保存なら false
     */
    async saveBody(isApply) {
      // 見ているだけのつもりで押してしまった人を止める（サブタスク「雇用指示登録申請」の部署の人でなければ確かめる）
      if (!await this.confirmDepartment(AKBS_SUB_TASK.EMPLOYMENT, isApply ? '申請' : '一時保存')) {
        return
      }

      // 申請時は必須チェック（一時保存は未入力があってもよい）
      if (isApply) {
        const errors = this.requiredErrors()
        if (errors.length) {
          // どこが足りないか見えるよう、全て開いてから知らせる
          this.allOpen()
          await this.errorDialog('申請できません', '未入力の必須項目があります。\n\n・' + errors.join('\n・'))
          return
        }

        // 面接から開いた申請は、面接タスクの採否が「採用」でなければ申請できない（採否は面接タスクで決める。一時保存はできる）
        if (!await this.isInterviewAdopted()) {
          await this.errorDialog('申請できません', '面接タスクの採否が「採用」になっていません。\n面接タスクで採否を「採用」にして保存してから、申請してください。')
          return
        }
      }

      // 矛盾チェック（一時保存でも、入力してある値がおかしければ保存しない）
      if (!await this.validate(isApply)) {
        return
      }

      this.loading = true
      try {
        // 変わったところにフラグを立ててから保存データを作る
        // （バックがメインタスクに控える物件名を、変更のあった雇用指示の行だけに絞るため。AKBS登録時にも立て直す）
        this.markChanged()

        const val = this.toSaveData()
        val.status = isApply ? AKBS_STATUS.PENDING : AKBS_STATUS.SAVED
        val.applicationDate = isApply ? utils.getToday() : (val.applicationDate || null)
        // 作成者は「最後に申請・一時保存を押した人」（初回だけではなく毎回入れ替える）
        val.creator = this.user
        val.creatorId = this.$store.getters.user.id

        // 期待する状況：一時保存・差戻・削除（未保存で行が無いときも可）。AKBS登録済みの行を上書きしないため
        const res = await backend.postData('akbsInputForm/save', { val, rp: this.rp, expectedStatus: [AKBS_STATUS.SAVED, AKBS_STATUS.RETURNED, AKBS_STATUS.DELETED] })
        if (await this.isRefused(res)) {
          return
        }

        // 申請したあとは次にどこへ行くか選んでもらう（この画面に残る・AKBS申請一覧・メインタスク）
        if (isApply) {
          // 面接だけの申請（まだ雇い入れが無い）なら、面接タスクを完了にして雇い入れを作るか聞く
          await this.finishInterview()

          const next = await dialogs.showMesssageDialog('申請しました', '承認をお待ちください。\n承認は「雇用指示権限」を持っている人なら誰でもできます。ただし、作成者本人は承認できません。\n\n遷移先を選択してください。', 'success', [
            { kind: 'normal', text: 'このまま', result: 'stay' },
            { kind: 'primary', text: '申請一覧へ', result: 'list' },
            { kind: 'secondary', text: 'メインタスクへ', result: 'main' },
          ])
          if (next === 'list') {
            this.toList()
            return
          }
          if (next === 'main') {
            // メインタスクが分からないことは無いはずだが、念のため無ければ一覧へ
            this.$router.push(this.mainTaskRoute() || { name: 'AkbsPendingList' })
            return
          }
        } else {
          await dialogs.showSuccessDialog('一時保存しました', '続きはこの画面で入力できます。')
        }

        // この画面に残るときは、保存したものを取り直す（IDや状況を画面に反映する）
        await this.getData()
      } catch (e) {
        console.log(e)
        await this.errorDialog('保存エラー', '保存に失敗しました。もう一度お試しください。')
      } finally {
        this.loading = false
      }
    },

    /**
     * 面接から開いた申請（雇い入れがまだ無い）で、面接タスクの採否が「採用」か
     * 画面を開いたあとに面接タスクで採否を変えていることがあるので、今の面接タスクを取り直して見る（取り直せなければ開いたときの値）
     * 取り直した採否は rp にも入れる（このあとの finishInterview が同じ値を見るため）
     * 雇い入れから開いた申請（employmentId あり）は面接の採否と関係ないので true
     * @returns {Boolean} 申請してよければ true
     */
    async isInterviewAdopted() {
      if (!this.val.interviewsId || this.val.employmentId) {
        return true
      }
      const latest = await requestPostManager.getRequestPost(this, this.val.interviewsId)
      if (latest) {
        this.rp.interviewsResult = latest.interviewsResult
        this.rp.status = latest.status
      }
      const rp = this.rp || {}
      return !!(rp.interviewsResult && rp.interviewsResult[0] === '採用')
    },

    /**
     * 面接タスクだけで、まだ雇い入れが無いときに申請したら、面接タスクを完了にして雇い入れタスクを作る（ユーザー決定 2026-09-23）
     * ・対象：面接から開いた申請（interviewsId あり・employmentId 無し）で、面接タスクがまだ完了していないとき
     * ・採否が「採用」でなければ何もしない（申請は isInterviewAdopted で採用のときしか通さないので、ここに来るのは念のため）
     * ・確認ダイアログ「面接タスクも完了しますか？」。未完了のサブタスクがあれば名前を出して、一緒に完了にする
     * ・面接タスクを完了 → 雇い入れを自動作成（requestPostManager.autoEmployTaskSave。面接タスクを完了にしたときと同じ道：
     *   setEmployFromInterview → register（空の枠をこの申請につなげる：req_akbs の面接の行の employment_id が埋まる）→ サブタスク → チャット通知）
     * ・作ったあとは、この申請は雇い入れにつながる（メインタスクへの遷移先も雇い入れになる）
     * @returns {Boolean} 雇い入れを作ったら true
     */
    async finishInterview() {
      const rp = this.rp || {}
      if (!this.val.interviewsId || this.val.employmentId || rp.status === 'done') {
        return false
      }
      if (!rp.interviewsResult || rp.interviewsResult[0] !== '採用') {
        await dialogs.showInfoDialog('面接タスクは完了していません', '面接タスクの採否が「採用」になっていないため、面接タスクの完了と雇い入れタスクの作成はしていません。\n面接タスクで採否を「採用」にして完了にすると、雇い入れタスクが作られます。')
        return false
      }

      const subTasks = (await taskRequestManager.getByRequestPostId(this, this.val.interviewsId)) || []
      const open = subTasks.filter(t => t.status !== 'done')
      const openText = open.length ? `\n\n未完了のサブタスク（${open.map(t => t.requestTask).join('、')}）も一緒に完了にします。` : ''
      const dialog = await dialogs.showConfirmDialog(
        '面接タスクも完了しますか？',
        `この申請は面接タスクから作ったもので、まだ雇い入れタスクがありません。\n面接タスクを完了にして、雇い入れ（新規入社）のタスクを作りますか？${openText}`,
        '完了して雇い入れを作る',
        '今はしない'
      )
      if (dialog !== 'YES') {
        return false
      }

      this.loading = true
      try {
        // 未完了のサブタスクを完了にする
        for (const task of open) {
          task.status = 'done'
          task.doneDate = moment().format('YYYY-MM-DD HH:mm:ss')
          task.doneStaffName = this.$store.getters.user.user_name
          await taskRequestManager.update(this, task)
        }
        // 面接タスクを完了にする
        const done = await requestPostManager.markAsDone(this, this.val.interviewsId)
        if (!done) {
          await this.errorDialog('完了エラー', '面接タスクを完了にできませんでした。メインタスクの画面から完了にしてください。')
          return false
        }
        // 雇い入れを作る（面接タスクを完了にしたときと同じ）
        const saved = await requestPostManager.autoEmployTaskSave(this, rp)
        if (!saved) {
          await this.errorDialog('雇い入れタスクを作れませんでした', '面接タスクは完了にしましたが、雇い入れタスクを作れませんでした。\nメインタスク一覧から雇い入れ（清掃員）を手で登録してください。')
          return false
        }
        // この申請は作った雇い入れにつながった（遷移先のメインタスクも雇い入れにする）
        this.val.employmentId = saved.id
        this.rp.status = 'done'
        await dialogs.showSuccessDialog('面接タスクを完了しました', `雇い入れタスク（No.${saved.requestNo}）を作りました。\nこの申請はその雇い入れタスクにつながっています。`)
        return true
      } catch (e) {
        console.log(e)
        await this.errorDialog('エラー', '面接タスクの完了か雇い入れタスクの作成に失敗しました。メインタスクの画面をご確認ください。')
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * ログインユーザーの主な部署が、そのサブタスクの部署と違えば、このまま進めてよいか確かめる
     * （見ているだけのつもりで「一時保存」「申請」「AKBSへ登録」を押してしまうことがあるため。ユーザー指示）
     * サブタスクは右のパネルで直している途中のもの（未保存でも画面に見えている部署）を見る。該当のサブタスクが無い・部署が空なら確かめない
     * @param {String} taskName AKBS_SUB_TASK.EMPLOYMENT（一時保存・申請）か EMPLOYMENT_APPROVE（AKBSへ登録）
     * @param {String} action ダイアログに出す操作の言葉（一時保存・申請・AKBSへ登録）
     * @returns {Boolean} 進めてよければ true
     */
    async confirmDepartment(taskName, action) {
      const panel = this.$refs.subTaskPanel
      const tasks = (panel && panel.tasks) || this.subTasks || []
      const task = tasks.find(t => t && !t.deleted && t.status !== 'remove' && isAkbsSubTask(t.requestTask, taskName))
      if (!task || (!task.departmentId && !task.departmentName)) {
        return true
      }
      const me = this.$store.getters.loginUserData || {}
      // ID がそろっていれば ID で、無ければ部署名で比べる（建物管理部と勤怠採用課は同じ扱い）
      if (isSameDepartmentForAkbs(task, me)) {
        return true
      }
      const dialog = await dialogs.showWarningConfirmDialog(
        `${action}してよいですか？`,
        `サブタスク「${task.requestTask}」の部署は「${task.departmentName || '未設定'}」です。\nあなたの部署（${me.departmentName || '不明'}）と違います。\n\nこのまま${action}しますか？`,
        `${action}する`
      )
      return dialog === 'YES'
    },

    /**
     * AKBSへ登録（承認）
     * 申請と同じチェックを通したうえで、取得時から変わったところにフラグを立ててバックへ渡す
     * バックはフラグの付いた項目・行だけを本番のAKBSへ書く（新規入社は元データが無いので全部書く）
     */
    async register() {
      // 二度押しを防ぐため、押した直後にマスクを掛ける（save と同じ）
      if (this.loading) return
      this.loading = true
      try {
        await this.registerBody()
      } finally {
        this.loading = false
      }
    },

    /**
     * AKBSへ登録の中身（register から呼ぶ。マスクは register が掛ける）
     */
    async registerBody() {
      // 見ているだけのつもりで押してしまった人を止める（サブタスク「雇用指示承認」の部署の人でなければ確かめる）
      if (!await this.confirmDepartment(AKBS_SUB_TASK.EMPLOYMENT_APPROVE, 'AKBSへ登録')) {
        return
      }

      const errors = this.requiredErrors()
      if (errors.length) {
        this.allOpen()
        await this.errorDialog('登録できません', '未入力の必須項目があります。\n\n・' + errors.join('\n・'))
        await this.approverCannotFix()
        return
      }

      if (!await this.validate(true, '登録')) {
        // 承認者の画面は閲覧のみなので、エラーで止まっても直せない（申請のあとに AKBS の内容が変わったときなど）。差戻してもらう
        await this.approverCannotFix()
        return
      }

      // 申請のあとに、契約詳細入力画面からこの申請の「契約詳細の変更内容」の中身が変わっていたら確かめてもらう
      // （申請者が気づかないまま登録すると、その分の雇用指示が別の申請になり、清掃員への書類送付が2回に分かれる）
      // 画面を開いている間に書かれることもあるので、登録の直前に取り直す
      await this.loadContractNotices()
      if (this.notesAfterApply) {
        const todos = this.contractNoticeTodos.map(n => `・${n.siteName} 契約コード ${n.cdContract}：契約終了 ${this.slash(n.endDate)}`)
        // 枠は、今も申請されている変更があるときだけ出ている。変更なしに戻された（取り消された）ときは枠が無いので、そう伝える
        const where = this.contractNoticeRows.length
          ? '画面の上の「契約詳細の変更が申請されています」の枠を確かめてください。'
          : '契約詳細の変更の申請が、取り消されたか、変更なしに戻されています（画面の上の枠は出ていません）。'
        const text = `申請のあとに、契約詳細の変更内容が書き換わっています。\n申請者が気づいていないかもしれません。${where}\n`
          + (todos.length ? `\n作業終了がまだの契約\n${todos.join('\n')}\n` : '')
          + '\nこの申請に含めるときは、差戻して直してもらってください。\nこのまま登録しますか？'
        const after = await dialogs.showConfirmDialog('申請のあとに契約詳細が変わっています', text, 'このまま登録へ進む')
        if (after !== 'YES') {
          return
        }
      }

      const dialog = await dialogs.showConfirmDialog('AKBSへ登録します','変更した内容を本番のAKBSへ書き込みます。よろしいですか？', '登録する')
      if (dialog !== 'YES') {
        return
      }

      this.loading = true
      try {
        // 変わったところにフラグを立ててから保存データを作る（フラグは akbs に載るので一緒に渡る）
        this.markChanged()

        const val = this.toSaveData()
        // 承認者情報（req_akbs の approver / approver_id。承認日時 approved_date はバックで now() を入れる）
        val.status = AKBS_STATUS.REGISTERED
        val.approver = this.user
        val.approverId = this.$store.getters.user.id

        // 期待する状況：承認待ち。申請取消された・既に登録済みなら本番には書かない（二重登録を防ぐ）
        // あわせて画面を開いたときの更新日時も送り、確認している間に誰かが保存していれば（申請取消→修正→再申請など）古い内容で登録しない
        // 開いたときの AKBS の賃金・終了日（expected）も送り、その間に賃金一括変更などで変わっていれば古い控えで書かない
        const res = await backend.postData('akbsInputForm/saveAKBS', { val, rp: this.rp, expectedStatus: [AKBS_STATUS.PENDING], expectedUpdateDate: this.val.updateDate, expected: this.expectedAkbs() })
        if (await this.isRefused(res)) {
          return
        }

        // サブタスク「雇用指示承認」はバックが完了にしている。それが最終完了タスクなら、雇い入れのメインタスクを完了にする
        // （未完了のサブタスクが残っていれば、最終完了タスクをそちらへ付け替える。雇い入れのメインタスクがまだ無い申請（面接だけ）は何もしない）
        let finishedText = ''
        if (val.employmentId) {
          const finished = await finishAfterAkbs(this, { _id: val.employmentId, type: '', requestNo: this.rp.requestNo }, AKBS_SUB_TASK.EMPLOYMENT_APPROVE)
          if (finished.mainDone) {
            finishedText = '\n\nサブタスクが全て完了したので、メインタスクも完了しました。'
          } else if (finished.movedTo) {
            finishedText = `\n\n未完了のサブタスクが残っているので、「最終完了タスク」を「${finished.movedTo}」へ付け替えました。`
          }
        }

        // 休み・代勤・振替で確認してもらうものがあれば、一覧へ戻らずモーダルで選んでもらう（保存後に一覧へ戻る）
        const workingCheckList = res.data.data.workingCheckList
        if (workingCheckList && workingCheckList.list && workingCheckList.list.length) {
          await dialogs.showSuccessDialog('AKBSへ登録しました', '変更した内容を本番のAKBSへ書き込みました。\n続けて、休み・代勤・振替の確認をお願いします。' + finishedText)
          this.val.workingCheckList = workingCheckList
          this.workingCheck = true
          return
        }

        await dialogs.showSuccessDialog('AKBSへ登録しました', '変更した内容を本番のAKBSへ書き込みました。' + finishedText)

        // 登録できたらこの画面に用は無いので、取り直さずに一覧へ戻る
        this.toList()
      } catch (e) {
        console.log(e)
        await this.errorDialog('登録エラー', 'AKBSへの登録に失敗しました。もう一度お試しください。')
        // 失敗したときだけ、今の状態を取り直す
        await this.getData()
      } finally {
        this.loading = false
      }
    },

    /**
     * 休み・代勤・振替の確認モーダルで操作を保存した（バックで実行済み）
     * 控えを画面に持ってからモーダルを閉じ、一覧へ戻る
     * @param {Object} workingCheckList { savedName, list }（list の各件に選んだ action が入っている）
     */
    savedWorkingCheck(workingCheckList) {
      this.val.workingCheckList = workingCheckList
      this.workingCheck = false
      this.toList()
    },

    /**
     * 差戻（承認者が申請者へ戻す。状況を「差戻」にするだけで、内容は書き換えない）
     * 本番のAKBSには何も書かない。押した時点でまだ承認待ちかをバックで確かめる
     */
    async sendBack() {
      // 差戻理由は無くても差し戻せるが、書き忘れに気づけるよう確認の文を変える
      const reason = (this.newReturnReason || '').trim()
      const dialog = await dialogs.showWarningConfirmDialog(
        reason ? 'この申請を差し戻します' : '差戻理由が記入されていません',
        reason ? '差し戻してよろしいでしょうか？' : '理由が記入されていませんがよろしいでしょうか？',
        '差し戻す'
      )
      if (dialog !== 'YES') {
        return
      }

      // 今回の分を「理由＋改行＋ユーザー名 日時」にして、過去の分の上に空行を挟んで積む（理由が無いときはその旨を残す）
      const entry = `${reason || '＊＊理由記載なし＊＊'}\n${this.user} ${moment().format('YYYY/MM/DD HH:mm')}`
      const returnReason = this.val.returnReason ? `${entry}\n\n${this.val.returnReason}` : entry

      // 期待する状況：承認待ち。差し戻せたらこの画面に用は無いので一覧へ戻る（失敗時は changeStatus が取り直す）
      if (await this.changeStatus(AKBS_STATUS.RETURNED, [AKBS_STATUS.PENDING], '差し戻しました', 'この申請は差戻になりました。', '差戻エラー', '差戻に失敗しました。もう一度お試しください。', { returnReason })) {
        this.toList()
      }
    },

    /**
     * 申請取消（承認待ちを一時保存に戻して、また直せるようにする）
     * 押した時点でまだ承認待ちかをバックで確かめる。既にAKBSへ登録済みなら戻せない
     */
    async cancelApply() {
      const dialog = await dialogs.showConfirmDialog('申請を取り消します', '一時保存に戻して、内容を直せるようにします。\n直したあとは、もう一度「申請」を押してください。\n\nよろしいですか？', '取り消す')
      if (dialog !== 'YES') {
        return
      }

      // 期待する状況：承認待ち。取り消せたら続けて直せるよう、この画面のまま取り直す
      if (await this.changeStatus(AKBS_STATUS.SAVED, [AKBS_STATUS.PENDING], '申請を取り消しました', '一時保存に戻りました。内容を直したら、もう一度「申請」を押してください。', '取消エラー', '申請の取消に失敗しました。もう一度お試しください。')) {
        await this.getData()
      }
    },

    /**
     * 申請を削除する（ユーザー決定 2026-09-23）
     * 保存していた内容を消して状況を「削除」にし、一覧へ戻る。誰でも押せる（誰が消したかは DB に残る）
     * 復活は、メインタスクかサブタスクの「雇用指示へ登録」で開き直すだけ（未保存と同じ状態から始まる。メインタスクが完了していれば復活できない）
     * 「削除を取り消す」は無い。削除した申請は一覧に出ない
     * サブタスク「雇用指示登録申請」「雇用指示承認」は未完了に戻るので、メインタスクを片付けるときはサブタスク画面でそれらを削除してもらう
     * API: akbsInputForm/deleteRequest
     */
    async deleteRequest() {
      const dialog = await dialogs.showWarningConfirmDialog(
        'この申請を削除します',
        '入力した内容は消えます（元に戻せません）。\nもう一度この清掃員の申請が要るときは、メインタスクかサブタスクの「雇用指示へ登録」から開き直してください。\n\nメインタスクを完了にする場合は、サブタスク「雇用指示登録申請」「雇用指示承認」をサブタスク画面で削除してください。\n\n削除してよろしいですか？',
        '削除する'
      )
      if (dialog !== 'YES') {
        return
      }

      this.loading = true
      try {
        // 未保存の枠には id が無いので、雇い入れ・面接の id と紐づけの情報も一緒に送る（行が無ければバックが削除の行を作る）
        const res = await backend.postData('akbsInputForm/deleteRequest', {
          id: this.val.id,
          employmentId: this.val.employmentId || '',
          interviewsId: this.val.interviewsId || '',
          staff: (this.akbs && this.akbs.nameStaff) || this.rp.employmentName || '',
          staffDailyCleaning: (this.akbs && this.akbs.staffDailyCleaning) || this.rp.employmentStaffNumber || '',
          requestNo: this.rp.requestNo || '',
          contractFormId: this.rp.akbsContractFormId || '',
          updateUser: this.$store.getters.user.id
        })
        if (await this.isRefused(res)) {
          return
        }
        await dialogs.showSuccessDialog('申請を削除しました', '申請一覧へ戻ります。')
        this.toList()
      } catch (e) {
        console.log(e)
        await this.errorDialog('削除エラー', '申請の削除に失敗しました。もう一度お試しください。')
        await this.getData()
      } finally {
        this.loading = false
      }
    },

    /**
     * 状況だけを変える（差戻・申請取消）
     * 内容は書き換えないため、val の id と新しい状況だけをバックへ渡す
     * @param {String} status 変えたい状況（AKBS_STATUS の値）
     * @param {Array} expectedStatus 今の状況として期待するもの（違えばバックは何もせず refused を返す）
     * @param {String} okTitle 成功時のダイアログの題
     * @param {String} okMessage 成功時のダイアログの文
     * @param {String} ngTitle 失敗時のダイアログの題
     * @param {String} ngMessage 失敗時のダイアログの文
     * @param {Object} extra 状況と一緒に保存するもの（差戻のときの { returnReason }。無ければ状況だけ）
     * @returns {Boolean} 変えられたら true。拒否・失敗のときは取り直したうえで false（そのあとどうするかは呼び元が決める）
     */
    async changeStatus(status, expectedStatus, okTitle, okMessage, ngTitle, ngMessage, extra) {
      this.loading = true
      try {
        const res = await backend.postData('akbsInputForm/changeStatus', { id: this.val.id, status, expectedStatus, updateUser: this.$store.getters.user.id, ...extra })
        if (await this.isRefused(res)) {
          return false
        }

        // 差戻のときはバックが作成者へチャットを送る。送れなかったときは理由を添えて、直接連絡してもらう
        await dialogs.showSuccessDialog(okTitle, okMessage + this.chatResultText(res.data && res.data.data && res.data.data.chat))
        return true
      } catch (e) {
        console.log(e)
        await this.errorDialog(ngTitle, ngMessage)
        // 失敗したときだけ、今の状態を取り直す
        await this.getData()
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * 差戻のチャット通知の結果を、成功ダイアログに添える文（通知していないときは空）
     * @param {Object} chat バックが返した { sent, reason }
     */
    chatResultText(chat) {
      if (!chat) {
        return ''
      }
      return chat.sent ? `\n\n作成者（${this.val.creator || ''}）へチャットで知らせました。` : `\n\n作成者へのチャットは送れませんでした（${chat.reason}）。\n直接連絡してください。`
    },

    /**
     * AKBS申請一覧へ戻る（AKBSへ登録・差戻のあと。一覧のドロップダウンからも使う）
     */
    toList() {
      this.$router.push({ name: 'AkbsPendingList' })
    },

    /**
     * 右上の「一覧」ドロップダウンで選んだ一覧へ移る
     * メインタスク一覧・サブタスク一覧はこの申請のメインタスクの番号で絞って開く（見積画面と同じ渡し方）
     * @param {Object} menuItem 選んだ項目（LIST_MENU の1つ）
     */
    selectList(menuItem) {
      if (menuItem.value === 'akbs') {
        this.toList()
        return
      }
      const name = menuItem.value === 'main' ? 'RequestPostList' : 'RequestPostProgressList'
      this.$router.push({ name })
    },

    /**
     * バックが「今の状況が期待と違う」として何もしなかったか
     * （ルートは { data: result } で返し、result が { refused: true, status: '今の状況' } のとき）。そのときは知らせて画面を取り直す
     * 別の人が先に申請取消・AKBS登録などをした場合に起きる
     * @param {Object} res backend.postData の応答
     */
    async isRefused(res) {
      const data = res && res.data && res.data.data
      if (!data || !data.refused) {
        return false
      }

      if (data.reason === 'akbs' && data.stale) {
        // この申請で賃金を変えた行の本番の賃金が、申請のあとに変わっている（賃金一括変更など）。開き直しても差し替わらないので、賃金を入れ直してもらう
        await dialogs.showWarningDialog(
          'AKBSの賃金が変わっています',
          `この申請で賃金を変えた行「${data.name || ''}」の本番の${data.item || '賃金'}が、申請のあとに変わっています。\nこのまま登録すると本番の変更を消してしまうので、登録しませんでした。\n差し戻して、賃金の変更を取り消してから保存し、開き直して最新の賃金の上で入れ直してもらってください。`
        )
      } else if (data.reason === 'akbs') {
        // 画面を開いてから本番の AKBS の賃金・終了日が変わっている（賃金一括変更など）。取り直すと最新に差し替わる
        await dialogs.showWarningDialog(
          'AKBSの内容が変わっています',
          `確認している間に本番のAKBSの${data.item || '賃金'}が変わったようです。\n今の操作は行いませんでした。最新の内容を表示します。`
        )
      } else if (data.reason === 'updated') {
        // 状況は同じでも、画面を開いてから誰かが保存している（誰がいつ変えたかを出す）
        const user = (this.$store.getters.userList || []).find(u => u.id == data.updateUser)
        const name = user ? utils.deleteKana(user.user_name) : '誰か'
        const when = data.updateDate ? moment(data.updateDate).format('M/D HH:mm') : ''
        await dialogs.showWarningDialog(
          '内容が変更されています',
          `確認している間に ${name} さんが ${when} に変更されたようです。\n今の操作は行いませんでした。画面を更新します。`
        )
      } else {
        await dialogs.showWarningDialog(
          '状況が変わっています',
          `この申請は、別の人の操作で「${data.status || '不明'}」になっています。\n今の操作は行いませんでした。最新の内容を表示します。`
        )
      }

      await this.getData()
      return true
    },


    /**
     * 保存・登録でバックへ渡す入力画面の情報（req_akbs）を作る
     * フリガナの半角化と勤務形態のDB形式への変換もここで行う
     */
    toSaveData() {
      // フリガナはAKBSに合わせて半角カタカナにする（画面の表示も変えて、変換されたことが分かるようにする）
      this.akbs.nameKanaStaff = this.toHankakuKana(this.akbs.nameKanaStaff)
      for (const row of this.akbs.emergency || []) {
        row.kana = this.toHankakuKana(row.kana)
      }

      // interviewsId / employmentId は getData で入れている（未保存なら from に従って、保存済みなら req_akbs の値）
      const val = { ...this.val, akbs: this.toDbAkbs() }
      val.type = this.type
      // 種別名は画面の表示（AKBS_TYPE_NAMES）と同じものを req_akbs.type_name に控える（一覧などで数値を引き直さずに出せる）
      val.typeName = this.typeName
      val.updateUser = this.$store.getters.user.id

      return val
    },

    /**
     * 画面の akbs をDBに入れる形にした複製を作る
     * 勤務形態は画面では選択肢のオブジェクトで持っているため、DBの形（コードの配列）に戻す
     */
    toDbAkbs() {
      const akbs = utils.clone(this.akbs)
      for (const row of akbs.employment || []) {
        for (const w of WEEK_LIST) {
          row['attendance' + w.id] = (row['attendance' + w.id] || []).map(a => a.id)
        }
      }
      return akbs
    },

    /**
     * 入力内容に矛盾がないか確認する（あればダイアログを出して false）
     * 未入力かどうかは requiredErrors で見るので、ここでは入力してある値どうしの矛盾だけを見る
     * ただし勤務時間の未入力など、申請時だけ止めるものは isApply で切り替える
     * 金額（賃金・交通費）のチェックはまだ入れていない
     * @param {Boolean} isApply 申請なら true、一時保存なら false
     * @param {String} confirmWord 確認ダイアログに出す言葉（AKBSへ登録のときは「登録」。省略時は「申請」）
     */
    async validate(isApply, confirmWord = '申請') {
      const akbs = this.akbs
      this.confirmWord = confirmWord

      // 前回のNGは消してからやり直す
      this.errorKeys = []
      this.minWageNotice = null
      this.validateError = false

      // 〒・携帯TEL などの形式（入力されていれば形式が違うとNG。change 時の赤字と同じ判定）
      for (const section of SECTIONS) {
        const rows = this.rows(section)
        for (let i = 0; i < rows.length; i++) {
          for (const item of this.items[section.key]) {
            const err = this.errText(item, rows[i])
            if (err) {
              const caption = this.plainCaption(item)
              const name = section.repeat ? `${section.caption} ${i + 1}件目の${caption}` : caption
              await this.errorDialog(name + 'の形式が不正', err)
              return this.ng(`${section.key}.${i}.${item.key}`)
            }
          }
        }
      }

      // 生年月日（年齢がありえない値）
      if (akbs.birthday) {
        const age = this.age(akbs.birthday)
        if (age !== null && (age < 10 || age >= 100)) {
          await this.errorDialog('生年月日をご確認ください', `年齢が${age}歳 になっています。\n生年月日に間違いがないかご確認ください。`)
          return this.ng('staff.0.birthday')
        }
      }

      // 生年月日と入社日の関係（生年月日が入社日より後、入社時に15歳未満）
      if (akbs.birthday && akbs.dateHire) {
        if (akbs.birthday > akbs.dateHire) {
          await this.errorDialog('生年月日が不正', `生年月日（${akbs.birthday}）が入社日（${akbs.dateHire}）より後になっています。`)
          return this.ng('staff.0.birthday', 'staff.0.dateHire')
        }
        const ageAtHire = this.age(akbs.birthday, akbs.dateHire)
        if (ageAtHire !== null && ageAtHire < 15) {
          await this.errorDialog('生年月日をご確認ください', `入社日（${akbs.dateHire}）時点で${ageAtHire}歳になっています。\n生年月日か入社日に間違いがないかご確認ください。`)
          return this.ng('staff.0.birthday', 'staff.0.dateHire')
        }
      }

      // 退職日が入社日より前
      if (akbs.dateHire && akbs.dateResignation && akbs.dateResignation < akbs.dateHire) {
        await this.errorDialog('退職日が不正', `退職日（${akbs.dateResignation}）が入社日（${akbs.dateHire}）より前になっています。`)
        return this.ng('retire.0.dateResignation')
      }

      // 退職理由だけ入っていて退職日が無い（退職日があれば理由は必須チェックで見ている）
      if (akbs.retireReason && !akbs.dateResignation) {
        await this.errorDialog('退職日が未入力', '退職理由が入っていますが、退職日がありません。\n退職日を入力するか、退職理由を消してください。')
        return this.ng('retire.0.dateResignation')
      }

      // 雇用指示一覧・編集
      const list = akbs.employment || []
      for (let i = 0; i < list.length; i++) {
        const row = list[i]
        const name = this.employmentName(i)

        // 退職日より後に始まる保存済みの雇用指示（物件を追加したが始まる前に辞めた等）は、1日も勤務が無いので取り消すか聞く
        // OK なら取消（AKBS 登録で本体から取消の表へ移る）。取り消さないなら今までどおり止める
        if (akbs.dateResignation && row.sequenceNumber && !row.isCancelled && !row.isChangedFrom && !row.isChangedTo && row.startDateWork && row.startDateWork > akbs.dateResignation) {
          // 承認者は申請の内容を変えない（取消にしない）。止めて差戻してもらう
          if (this.isApprover) {
            await this.errorDialog(name + 'の開始日が不正', `開始日（${row.startDateWork}）が退職日（${akbs.dateResignation}）より後になっています。`)
            return this.ng(`employment.${i}.startDateWork`)
          }
          const res = await dialogs.showConfirmDialog(
            name + 'は退職日より後に始まります',
            `開始日（${row.startDateWork}）が退職日（${akbs.dateResignation}）より後なので、この雇用指示は1日も勤務がありません。\nこの雇用指示を取り消しますか？（最初から無かったものになります）`,
            '取り消す'
          )
          if (res !== 'YES') {
            await this.errorDialog(name + 'の開始日が不正', `開始日（${row.startDateWork}）が退職日（${akbs.dateResignation}）より後になっています。\n取り消すか、退職日を直してください。`)
            return this.ng(`employment.${i}.startDateWork`)
          }
          this.$refs.employment.setCancelled(row, true)
        }

        // 取り消した行は無かった扱いなので、以降の矛盾チェックは見ない
        if (row.isCancelled) {
          continue
        }

        // 勤務を直せる行か（採番なし・上書き・契約内容変更の変更後。表の isEditable と同じ）
        // 直せない行でも矛盾は教える（修正するつもりが無くても、気づいて直せるように）。その代わり、どう直すかを一言足す
        const editable = !row.sequenceNumber || row.mode === 'edit' || row.mode === 'change'
        const hint = editable ? '' : this.fixHint(row)

        // 表示している行は全部見る（再入社は清掃員コードも新しく発行するので、過去の行も入社日と突き合わせる）
        // 開始日が入社日より前
        if (akbs.dateHire && row.startDateWork && row.startDateWork < akbs.dateHire) {
          await this.errorDialog(name + 'の開始日が不正', `開始日（${row.startDateWork}）が入社日（${akbs.dateHire}）より前になっています。`)
          return this.ng(`employment.${i}.startDateWork`)
        }

        // 終了日が開始日より前
        if (row.startDateWork && row.endDateWork && row.endDateWork < row.startDateWork) {
          // 契約内容変更の変更前の行は、終了日が変更後の開始日の前日で自動で入る。直すのは変更後の開始日
          const toIndex = row.isChangedFrom ? list.findIndex(e => e.isChangedTo && e.changeFromSequenceNumber == row.sequenceNumber) : -1
          if (toIndex >= 0) {
            await this.errorDialog(name + 'の変更後の開始日が不正', `変更後の開始日（${list[toIndex].startDateWork}）が、変更前の開始日（${row.startDateWork}）より前か同じ日になっています。\n変更後の開始日は変更前の開始日より後にしてください。`)
            return this.ng(`employment.${toIndex}.startDateWork`, `employment.${i}.endDateWork`)
          }
          await this.errorDialog(name + 'の終了日が不正', `終了日（${row.endDateWork}）が開始日（${row.startDateWork}）より前になっています。`)
          return this.ng(`employment.${i}.endDateWork`)
        }

        // 退職なら全物件の勤務が退職日までに終わる（終了日が無い、または退職日より後ならNG）
        if (akbs.dateResignation) {
          if (!row.endDateWork) {
            // 行の状態で終了日の入れ方が違うので、それに合わせて伝える
            let how = '終了日を入力してください。'
            if (row.isChangedFrom) {
              how = '変更後の開始日を入力すると、変更前の終了日が自動で入ります。'
            } else if (!editable && row.mode !== 'ended') {
              how = '作業終了にして終了日を入力してください。'
            }
            await this.errorDialog(name + 'の終了日が未入力', `退職日（${akbs.dateResignation}）がありますが、この勤務が終わっていません。\n${how}`)
            return this.ng(`employment.${i}.endDateWork`)
          }
          if (row.endDateWork > akbs.dateResignation) {
            // AKBS に元から退職日より後の終了日が入っていた行（契約終了など）はロック中で直せないので、上書きを案内する
            let how = '終了日を退職日までの日に直してください。'
            if (row.isChangedFrom) {
              how = '変更後の開始日が退職日より後になっています。変更後の開始日を直してください。'
            } else if (!editable && row.mode !== 'ended') {
              how = '上書きにしてから、終了日を退職日までの日に直してください。'
            }
            await this.errorDialog(name + 'の終了日が不正', `終了日（${row.endDateWork}）が退職日（${akbs.dateResignation}）より後になっています。\n${how}`)
            return this.ng(`employment.${i}.endDateWork`)
          }
        }

        // 作業終了にした行は終了日が要る（申請時のみ）
        if (isApply && row.mode === 'ended' && !row.endDateWork) {
          await this.errorDialog(name + 'の終了日が未入力', '作業終了の場合は終了日を入力してください。')
          return this.ng(`employment.${i}.endDateWork`)
        }

        // 賃金の並び（開いたときに AKBS の今の賃金を差し替えるので、申請で足した賃金と前後が合わなくなることがある。開いた直後にも同じ確認をして知らせている）
        const orderErrors = this.wageOrderErrors(list, i)
        if (orderErrors.length) {
          await this.errorDialog(orderErrors[0].title, orderErrors[0].message)
          return this.ng(...orderErrors[0].keys)
        }

        // 作業終了にした行と契約内容変更の変更前の行は、AKBS にある勤務のまま終わらせるだけなので、曜日ごとの勤務は見ない
        // （変更前の勤務は変更後の行に写してあるので、そちらで見る）。終了日まわりは上で見ている
        if (row.mode === 'ended' || row.isChangedFrom) {
          continue
        }

        // 曜日ごとの勤務（メッセージにどの行か分かるよう、曜日の前に行の名前を付ける）
        // ロック中の行でも矛盾はエラーで止める（気づいて直せるように）。確認だけのもの（休憩）は直している行だけ
        let hasWork = false
        // 拘束時間が6時間以下で休憩があるときの確認は、曜日ごとに出すと何回も出るので、行ごとに1回にまとめる
        const breakWarnings = []
        for (const w of WEEK_LIST) {
          const att = row['attendance' + w.id]
          const start = row['start' + w.id]
          const end = row['end' + w.id]
          const hours = row['workingHours' + w.id]

          // 勤務形態が無い曜日は休み
          if (!att || !att.length) {
            continue
          }
          hasWork = true
          const week = `${name}の${w.name}`
          const key = `employment.${i}.`

          // 勤務形態の矛盾（毎週と他の週の同時選択など）
          if (!await utilsAKBS.checkAttendance(att, week, hint)) return this.ng(key + 'attendance' + w.id)

          // 勤務時間の未入力は申請時だけ止める
          if (isApply) {
            if (!await utilsAKBS.checkNoTimes(start, week + '曜日の勤務時間', hint)) return this.ng(key + 'start' + w.id)
            if (!await utilsAKBS.checkNoTimes(end, week + '曜日の勤務時間', hint)) return this.ng(key + 'end' + w.id)
          }

          // 時間帯・実働の矛盾（両方入っているときだけ）
          if (start && end) {
            if (!await utilsAKBS.checkTimes(start, week, hint)) return this.ng(key + 'start' + w.id)
            if (!await utilsAKBS.checkTimes(end, week, hint)) return this.ng(key + 'end' + w.id)
            if (!await utilsAKBS.checkTimes2(start, end, week, hint)) return this.ng(key + 'start' + w.id, key + 'end' + w.id)
            // 実働時間は、申請時は必ず、一時保存は入力してあるときだけ見る
            if (isApply || hours) {
              const warn = []
              if (!await utilsAKBS.checkBreakTime(start, end, hours, week, warn, hint)) return this.ng(key + 'workingHours' + w.id)
              // 6時間以下で休憩ありの確認は、直している行だけ（触っていない行に申請のたびに聞かない）
              if (warn.length && editable) {
                breakWarnings.push({ name: w.name, key: key + 'workingHours' + w.id, ...warn[0] })
              }
            }
          }
        }

        // 拘束時間が6時間以下で休憩がある曜日をまとめて確認
        if (breakWarnings.length) {
          const lines = breakWarnings.map(x => `${x.name}曜日：拘束時間 ${x.hours} 時間、休憩 ${x.breakHours} 時間`).join('\n')
          if (await dialogs.showWarningConfirmDialog(name + 'の実働時間', `拘束時間が6時間以下ですが休憩時間が発生しています。\n${lines}\n\nお間違いないですか？`) != 'YES') {
            return this.ng(...breakWarnings.map(x => x.key))
          }
        }

        // 1日も勤務日が無い（申請時のみ。代勤専門は決まった勤務日が無いので見ない）
        if (isApply && !hasWork && row.cdSite != '000000000') {
          // 採番なしの行は削除できるが、ロック中の行は削除できないので、上書きか作業終了を案内する
          let how = '勤務を入力するか、この行を削除してください。'
          if (row.sequenceNumber) {
            how = editable ? '勤務を入力するか、作業終了にしてください。' : '上書きで勤務を入力するか、作業終了にしてください。'
          }
          await this.errorDialog(name + 'の勤務', `1日も勤務日がありません。\n${how}`)
          return this.ng(`employment.${i}.row`)
        }
      }

      // 1人が同じ時間に2か所は掃除できないので、行どうしで勤務時間が重なっていないか見る
      if (!await this.validateOverlap()) {
        return false
      }

      // 雇用契約書で週の休みを決めているので、1週間に休みが1日も無い形は登録できない
      if (!await this.validateWeek7()) {
        return false
      }

      // 金額（時給・交通費）
      if (!await this.validateFare(isApply)) {
        return false
      }

      // 契約情報との比較（申請時だけ）
      if (isApply && !await this.validateContract()) {
        return false
      }

      return true
    },

    /**
     * 雇用指示の祝日勤務・ゴミ搬出・全社祝日が、受注している契約より多くなっていないか
     * 契約より多いと会社の持ち出しになるため、申請時に確認する（NGではなく「よろしいですか？」）
     * 対象は作業終了日が無い行。代勤専門（物件コード 000000000）と契約情報が取れない行は見ない
     */
    async validateContract() {
      const table = this.$refs.employment
      if (!table) {
        return true
      }

      // 比べる項目（雇用指示のキー、契約のキー、名前、表示の仕方）
      const items = [
        // 祝日勤務は 0.25 刻みの小数もあるので、選択肢に無い値は「○時間」で出す
        { key: 'cdKubunHoliday', contractKey: 'holiday', name: '祝日勤務', text: v => this.selectionName(HOLIDAYTYPE_SELECTIONS, v) || `${Number(v)}時間` },
        { key: 'hasCarryingoutGarbage', contractKey: 'garbage', name: 'ゴミ搬出', text: v => this.flagText(v) },
        { key: 'hasExportingCompanysHoliday', contractKey: 'companyHoliday', name: '全社祝日出勤', text: v => this.flagText(v) },
      ]

      const list = this.akbs.employment || []
      for (let i = 0; i < list.length; i++) {
        const row = list[i]
        if (row.isCancelled || row.endDateWork || !row.cdSite || row.cdSite == '000000000' || !row.cdContract) {
          continue
        }

        const info = table.getSiteInfo(row.cdSite)
        const contract = info && info.contract ? info.contract.find(c => String(c.cdContract).trim() == String(row.cdContract).trim()) : null
        if (!contract) {
          continue
        }

        for (const item of items) {
          const value = row[item.key]
          const limit = contract[item.contractKey]
          if (this.isBlank(value) || this.isBlank(limit)) {
            continue
          }

          // 契約より多い（祝日勤務は時間・FULL、ゴミ搬出と全社祝日は あり／なし）ときだけ確認
          if (Number(value) > Number(limit)) {
            const res = await dialogs.showWarningConfirmDialog(
              `${item.name}が契約より多くなっています`,
              `${this.employmentName(i)}\n\n契約コード（${contract.cdContract}）の契約は、${item.name}が「${item.text(limit)}」ですが、「${item.text(value)}」で登録しようとしています。\n\nこのまま${this.confirmWord}してよろしいですか？`
            )
            if (res !== 'YES') {
              return this.ng(`employment.${i}.${item.key}`)
            }
          }
        }
      }

      // 物件全体の過不足時間との比較
      if (!await this.validateSiteShort()) {
        return false
      }

      return true
    },

    /**
     * 今回の申請で新しく足す勤務時間が、物件全体の不足時間に収まっているか（申請時の確認）
     * 対象は新規追加の行（未保存で、契約内容変更の変更後でもないもの）だけ
     * 修正や契約内容変更は他の清掃員との兼ね合いがあり差分では判断できないため見ない
     * 同じ物件が複数行あれば合計して1回だけ聞く
     */
    async validateSiteShort() {
      const table = this.$refs.employment
      if (!table) {
        return true
      }

      // 物件ごとに、今回足す時間と対象の行をまとめる
      const sites = {}
      const list = this.akbs.employment || []
      for (let i = 0; i < list.length; i++) {
        const row = list[i]
        if (row.isCancelled || row.endDateWork || !row.cdSite || row.cdSite == '000000000') {
          continue
        }
        if (!this.isNewSite(row)) {
          continue
        }
        const hours = Number(table.weekHours(row)) || 0
        if (!hours) {
          continue
        }
        if (!sites[row.cdSite]) {
          sites[row.cdSite] = { hours: 0, indexes: [] }
        }
        sites[row.cdSite].hours += hours
        sites[row.cdSite].indexes.push(i)
      }

      for (const cdSite of Object.keys(sites)) {
        const info = table.getSiteInfo(cdSite)
        if (!info || !info.siteShort || this.isBlank(info.siteShort.total)) {
          continue
        }

        const site = sites[cdSite]
        const short = Number(info.siteShort.total)
        const names = site.indexes.map(i => this.employmentName(i)).join('\n')
        let msg = ''

        // 不足が無い物件に足そうとしている
        if (short >= 0) {
          msg = `${names}\n\nこの物件は、現在、不足時間はありませんが、${site.hours}時間 を登録しようとしています。`

        // 不足より多く足そうとしている（不足は「-」で返る）
        } else if (site.hours > short * -1) {
          msg = `${names}\n\n1週間の契約との過不足時間が ${short}時間 に対し、${site.hours}時間 を登録しようとしています。`
        }

        if (msg) {
          const res = await dialogs.showWarningConfirmDialog('契約との時間差 確認', msg + `\n退職予定の清掃員がいる場合は無視してください。\n\nこのまま${this.confirmWord}してよろしいですか？`)
          if (res !== 'YES') {
            return this.ng(...site.indexes.map(i => `employment.${i}.row`))
          }
        }
      }

      return true
    },

    /**
     * 有り・無しの表示
     * @param {*} value 1／0
     */
    flagText(value) {
      return Number(value) === 1 ? '有り' : '無し'
    },

    /**
     * 金額（時給・交通費）に矛盾がないか（あればダイアログを出して false）
     * 対象は作業終了日が無い行の、今適用している賃金（終了日が無いもの）だけ
     * 高すぎる・安すぎる・2重払いは申請時だけ見て、NGではなく「よろしいですか？」の確認にする
     * @param {Boolean} isApply 申請なら true、一時保存なら false
     */
    async validateFare(isApply) {
      const list = this.akbs.employment || []

      // この申請で賃金を変えた行・足した行か（元データの賃金と突き合わせる。採番なしの行は足した行）
      // 「高すぎ・安すぎ・契約単価超え」の確認はこの行だけに出す。触っていない行に申請のたびに同じ確認を出さないため
      // エラー（マイナス・小数・区分と金額の不整合・最低賃金）と定期の2重払いの確認は、気づいて直せるよう触っていない行も見る
      const origRows = {}
      for (const e of (this.akbs.originalData && this.akbs.originalData.employment) || []) {
        if (e.sequenceNumber) {
          origRows[e.sequenceNumber] = e
        }
      }
      const isWageChanged = row => !row.sequenceNumber || this.wagesChanged(row, origRows[row.sequenceNumber])

      // 今の賃金を集める（作業終了した行と、適用が終わった賃金は見ない）
      const targets = []
      for (let i = 0; i < list.length; i++) {
        const row = list[i]
        if (row.isCancelled || row.endDateWork) {
          continue
        }
        // 賃金のみ変更で複数並んでいても、今の賃金は一番最後の1件だけ（賃金の行は適用日だけで、次の賃金の適用日の前日まで効く）
        const wages = row.wages || []
        const wi = wages.length - 1
        if (wi >= 0) {
          targets.push({ i, wi, wage: wages[wi], changed: isWageChanged(row) })
        }
      }

      for (const t of targets) {
        const wage = t.wage
        // 賃金のみ変更で2件並ぶときは何件目か分かるようにする
        const name = this.employmentName(t.i)
        const key = `employment.${t.i}.wage${t.wi}.`

        // 時給（マイナス・小数）
        const wageErr = this.amountError(wage.basicWage)
        if (wageErr) {
          await this.errorDialog(name + 'の時給が不正', `時給が${wageErr}。`)
          return this.ng(key + 'basicWage')
        }

        for (const fare of FARE_ITEMS) {
          const kubun = Number(wage[fare.typeKey]) || 0
          const kubunName = this.selectionName(PROVISION_SELECTIONS, kubun)
          const fee = Number(wage[fare.feeKey]) || 0
          const cap = `${name}の${fare.caption}`

          // マイナス・小数
          const feeErr = this.amountError(wage[fare.feeKey])
          if (feeErr) {
            await this.errorDialog(cap + 'の金額が不正', `${fare.caption}の金額が${feeErr}。`)
            return this.ng(key + fare.feeKey)
          }

          // 区分が1日・1ヶ月なのに0円
          if (kubun > 0 && fee === 0) {
            await this.errorDialog(cap + 'の金額が不正', `区分は「${kubunName}」ですが、金額が0円です。\n金額を入力するか、区分を「無」にしてください。`)
            return this.ng(key + fare.typeKey, key + fare.feeKey)
          }

          // 区分が無なのに1円以上
          if (kubun === 0 && fee > 0) {
            await this.errorDialog(cap + 'の区分が不正', `区分は「無」ですが、金額が${fee}円です。\n区分を選ぶか、金額を0円にしてください。`)
            return this.ng(key + fare.typeKey, key + fare.feeKey)
          }

          // 1日なのに高すぎる、1ヶ月なのに安すぎる（申請時に、賃金を変えた行だけ確認する。間違いとは限らないので確認にとどめる）
          if (!isApply || !t.changed) {
            continue
          }
          const limit = FARE_LIMITS[fare.key]
          if (kubun === 1 && fee > limit.dailyMax) {
            const res = await dialogs.showWarningConfirmDialog(`${fare.caption}の交通費をご確認ください`, `${name}\n\n${fare.caption}の区分は「1日」ですが、金額が${fee}円と高めです（目安：${limit.dailyMax}円まで）。\n区分が「1ヶ月」の間違いではありませんか？\n\nこのまま${this.confirmWord}してよろしいですか？`)
            if (res !== 'YES') {
              return this.ng(key + fare.typeKey, key + fare.feeKey)
            }
          }
          if (kubun === 2 && fee < limit.monthlyMin) {
            const res = await dialogs.showWarningConfirmDialog(`${fare.caption}の交通費をご確認ください`, `${name}\n\n${fare.caption}の区分は「1ヶ月」ですが、金額が${fee}円と安めです（目安：${limit.monthlyMin}円から）。\n区分が「1日」の間違いではありませんか？\n\nこのまま${this.confirmWord}してよろしいですか？`)
            if (res !== 'YES') {
              return this.ng(key + fare.typeKey, key + fare.feeKey)
            }
          }
        }
      }

      // 最低賃金（この申請で賃金を変えた行・足した行だけ）
      // 賃金を変えた行は開いたときに AKBS の今の値へ差し替えないので、賃金一括変更のあとでも下回ったままにならないように、ここで確かめてもらう
      // 対象日は「その雇用指示の開始日」と「今日」の遅い方（2026-09-24 ユーザー指示）。
      // 見るのは、対象日に効いている賃金（開始日 ≦ 対象日 ≦ 終了日。終了日なしは無期限）1件だけで、
      // 対象日が適用開始〜適用終了の中にある最低賃金と比べる。対象日に効いている賃金が無ければ比べない
      // 下回っていたら保存しない（一時保存も）。その時給の欄を赤くし、雇用指示一覧の上にその営業所の最低賃金マスタを出して直してもらう
      // 比べるのは、その行の営業所（東京／千葉）の最低賃金。営業所が空の行は比べない（必須なので申請時に別で止まる）
      const checkRows = list.map((row, i) => ({ row, i })).filter(({ row }) => !row.isCancelled && isWageChanged(row))
      if (checkRows.length) {
        const minWageRows = await this.getMinWageRows()
        const today = utils.getToday()
        for (const { row, i } of checkRows) {
          const pref = row.salesOffice
          if (!pref || !minWageRows[pref]) {
            continue
          }
          // 対象日（雇用指示の開始日と今日の遅い方）
          const target = row.startDateWork && row.startDateWork > today ? row.startDateWork : today
          // 対象日に効いている賃金＝対象日以前に始まった賃金のうち一番後ろのもの（適用日が無い賃金は見ない）
          const wages = row.wages || []
          let wi = -1
          for (let k = wages.length - 1; k >= 0; k--) {
            if (wages[k].startDate && wages[k].startDate <= target) {
              wi = k
              break
            }
          }
          const wage = wi >= 0 ? wages[wi] : null
          if (!wage || wage.basicWage === '' || wage.basicWage === null || wage.basicWage === undefined) {
            continue
          }
          const hit = minWageRows[pref].find(m => m.startDate <= target && (!m.endDate || m.endDate >= target))
          if (hit && Number(wage.basicWage) < Number(hit.minWage)) {
            const name = this.employmentName(i)
            const min = Number(hit.minWage).toLocaleString()
            // その営業所の最低賃金マスタを画面に出して、いつからいくら以上なのか分かるようにする（直して保存できたら消える）
            this.minWageNotice = { pref, rows: minWageRows[pref], name, from: hit.startDate, minWage: hit.minWage }
            await dialogs.showWarningDialog(
              name + 'の時給が最低賃金未満',
              `時給（${Number(wage.basicWage).toLocaleString()}円）が、${this.slash(hit.startDate)} からの ${pref} の最低賃金（${min}円）を下回っています。\n${min}円以上にしてください。`
            )
            return this.ng(`employment.${i}.wage${wi}.basicWage`)
          }
        }
      }

      // ここから先の確認は申請時だけ
      if (!isApply) {
        return true
      }

      // 契約単価より清掃員の「時給+交通費」の方が高い（超過分の計算は表の overWage と同じもの）。賃金を変えた行だけ
      const table = this.$refs.employment
      if (table) {
        for (const t of targets) {
          if (!t.changed) {
            continue
          }
          const row = list[t.i]
          const over = table.overWage(row, t.wage)
          if (!over) {
            continue
          }
          const name = this.employmentName(t.i)
          const res = await dialogs.showWarningConfirmDialog(
            '時給が契約単価を超えています',
            `${name}\n\n契約単価 ${table.formatNumber(table.contractWage(row))}円/h に対し、時給+交通費は ${table.formatNumber(table.hourlyWage(row, t.wage))}円/h で、${table.formatNumber(over)}円 高くなっています。\n\nこのまま${this.confirmWord}してよろしいですか？`
          )
          if (res !== 'YES') {
            const key = `employment.${t.i}.wage${t.wi}.`
            return this.ng(key + 'basicWage', ...FARE_ITEMS.map(fare => key + fare.feeKey))
          }
        }
      }

      // 別の物件で同じ種別の交通費を「1ヶ月」で同じ金額にしていると、定期代を2重に払うことになる
      for (let a = 0; a < targets.length; a++) {
        for (let b = a + 1; b < targets.length; b++) {
          const ta = targets[a]
          const tb = targets[b]
          if (ta.i === tb.i) {
            continue
          }

          for (const fare of FARE_ITEMS) {
            const feeA = Number(ta.wage[fare.feeKey]) || 0
            const feeB = Number(tb.wage[fare.feeKey]) || 0
            if (Number(ta.wage[fare.typeKey]) === 2 && Number(tb.wage[fare.typeKey]) === 2 && feeA > 0 && feeA === feeB) {
              const res = await dialogs.showWarningConfirmDialog(
                `${fare.caption}の交通費が2重になっていませんか？`,
                `${this.employmentName(ta.i)}\n${this.employmentName(tb.i)}\n\nどちらも${fare.caption}の区分が「1ヶ月」で、金額が同じ${feeA}円です。\n同じ定期を2回払うことになる場合は、片方を「無」にしてください。\n\nこのまま${this.confirmWord}してよろしいですか？`
              )
              if (res !== 'YES') {
                return this.ng(
                  `employment.${ta.i}.wage${ta.wi}.${fare.typeKey}`, `employment.${ta.i}.wage${ta.wi}.${fare.feeKey}`,
                  `employment.${tb.i}.wage${tb.wi}.${fare.typeKey}`, `employment.${tb.i}.wage${tb.wi}.${fare.feeKey}`
                )
              }
            }
          }
        }
      }

      return true
    },

    /**
     * 金額としておかしい値か（おかしければその説明、問題なければ空文字。未入力は見ない）
     * @param {*} value 金額
     */
    amountError(value) {
      if (this.isBlank(value)) {
        return ''
      }
      const num = Number(value)
      if (isNaN(num)) {
        return '数字ではありません'
      }
      if (num < 0) {
        return 'マイナスになっています'
      }
      if (!Number.isInteger(num)) {
        return '小数になっています'
      }
      return ''
    },

    /**
     * 選択肢の表示名（id から name を引く。無ければ空文字）
     * @param {Array} selections 選択肢（{ id, name } の配列）
     * @param {*} id 値
     */
    selectionName(selections, id) {
      const found = selections.find(s => s.id == id)
      return found ? found.name : ''
    },

    /**
     * 雇用指示の行どうしで勤務時間が重なっていないか（重なっていればダイアログを出して false）
     * 適用期間が重なり、同じ曜日で、週（毎週・第n・隔週）が重なり、時間帯も重なるときだけNG
     * 例：月曜 第1 9:00~10:00 と 月曜 第2 9:00~10:00 は週が違うのでOK
     */
    async validateOverlap() {
      const list = this.akbs.employment || []

      for (let i = 0; i < list.length; i++) {
        for (let j = i + 1; j < list.length; j++) {
          const a = list[i]
          const b = list[j]

          // 取り消した行は無かった扱い
          if (a.isCancelled || b.isCancelled) {
            continue
          }

          // 適用期間が重なっていなければ同時には働かない（終了日が無いものは続いている扱い）
          if (!this.periodOverlap(a, b)) {
            continue
          }

          for (const w of WEEK_LIST) {
            const attA = a['attendance' + w.id]
            const attB = b['attendance' + w.id]
            if (!attA || !attA.length || !attB || !attB.length) {
              continue
            }
            if (!this.weekOverlap(attA, attB)) {
              continue
            }

            const sA = a['start' + w.id]
            const eA = a['end' + w.id]
            const sB = b['start' + w.id]
            const eB = b['end' + w.id]
            if (!sA || !eA || !sB || !eB) {
              continue
            }
            if (!this.timeOverlap(sA, eA, sB, eB)) {
              continue
            }

            await this.errorDialog(
              `${w.name}曜日の勤務時間が重なっています`,
              `${this.employmentName(i)}：${sA}~${eA}\n${this.employmentName(j)}：${sB}~${eB}\n\n1人が同じ時間に2か所で働くことはできません。`
            )
            return this.ng(
              `employment.${i}.start${w.id}`, `employment.${i}.end${w.id}`, `employment.${i}.attendance${w.id}`,
              `employment.${j}.start${w.id}`, `employment.${j}.end${w.id}`, `employment.${j}.attendance${w.id}`
            )
          }
        }
      }

      return true
    },

    /**
     * 代勤専門以外の雇用指示を合わせて、1週間に休みが1日も無い（月〜日の全部に勤務がある）期間があれば止める
     * 雇用契約書で週の休みを決めているため。開始日・終了日を考慮し、同じ期間に効いている行だけを合わせる
     * 数えるのは勤務形態が「毎週」の曜日だけ（第1月曜・第2火曜のように週がばらければ、1週間に7日にはならない）
     * 取り消した行は無かった扱い。作業終了した行・契約内容変更の変更前の行も、終了日までは効いているので合わせる
     */
    async validateWeek7() {
      const list = this.akbs.employment || []
      const hasAtt = (row, w) => (row['attendance' + w.id] || []).some(a => a.id === '00')
      const rows = list
        .map((row, i) => ({ row, i }))
        .filter(({ row }) => !row.isCancelled && row.cdSite != '000000000' && WEEK_LIST.some(w => hasAtt(row, w)))
      if (!rows.length) {
        return true
      }

      // 期間の切れ目（開始日と、終了日の翌日）。切れ目から次の切れ目の前日までは、効いている行の顔ぶれが変わらない
      const NO_START = '0000-01-01'
      const points = new Set()
      for (const { row } of rows) {
        points.add(row.startDateWork || NO_START)
        if (row.endDateWork) {
          points.add(moment(row.endDateWork).add(1, 'day').format('YYYY-MM-DD'))
        }
      }
      const sorted = [...points].sort()

      for (let p = 0; p < sorted.length; p++) {
        const from = sorted[p]
        const active = rows.filter(({ row }) => (row.startDateWork || NO_START) <= from && (!row.endDateWork || row.endDateWork >= from))
        if (!active.length) {
          continue
        }
        const offDays = WEEK_LIST.filter(w => !active.some(({ row }) => hasAtt(row, w)))
        if (offDays.length) {
          continue
        }

        // 切れ目の次の前日まで（次が無ければずっと）
        const to = p + 1 < sorted.length ? moment(sorted[p + 1]).subtract(1, 'day').format('YYYY-MM-DD') : ''
        const period = (from === NO_START ? '' : this.slash(from)) + ' 〜 ' + (to ? this.slash(to) : '')
        const names = active.map(({ i }) => '・' + this.employmentName(i)).join('\n')
        await this.errorDialog(
          '1週間に休みがありません',
          `${names}\n\n上の雇用指示を合わせると、${period.trim()} の期間は月曜から日曜まで全部に「毎週」の勤務があり、1週間に休みが1日もありません。\n雇用契約書で週の休みを決めているので、このままでは${this.confirmWord}できません。\nどれかの曜日を休みにするか、開始日・終了日を見直してください。`
        )
        return this.ng(...active.map(({ i }) => `employment.${i}.row`))
      }

      return true
    },

    /**
     * 2つの雇用指示の適用期間が重なるか（開始日が無ければ最初から、終了日が無ければずっと続いている扱い）
     * @param {Object} a 雇用指示
     * @param {Object} b 雇用指示
     */
    periodOverlap(a, b) {
      const startA = a.startDateWork || '0000-00-00'
      const endA = a.endDateWork || '9999-12-31'
      const startB = b.startDateWork || '0000-00-00'
      const endB = b.endDateWork || '9999-12-31'
      return startA <= endB && startB <= endA
    },

    /**
     * 勤務形態（毎週・第n・隔週）が同じ週に当たるか
     * 毎週はどれとも重なる
     * 隔週どうしは交互に入る前提で重ならない扱い、隔週と他（毎週・第n）は毎週と同じで重なる扱い
     * @param {Array} attA 勤務形態（{ id, name } の配列）
     * @param {Array} attB 勤務形態（{ id, name } の配列）
     */
    weekOverlap(attA, attB) {
      const idsA = attA.map(a => a.id)
      const idsB = attB.map(a => a.id)

      // 隔週どうしだけはOK
      if (idsA.includes('10') && idsB.includes('10')) {
        return false
      }

      if (idsA.includes('00') || idsB.includes('00') || idsA.includes('10') || idsB.includes('10')) {
        return true
      }

      return idsA.some(id => idsB.includes(id))
    },

    /**
     * 時間帯が重なるか（終わりと始まりが同じ時刻なら重ならない）
     * @param {String} sA 開始（HH:mm）
     * @param {String} eA 終了（HH:mm）
     * @param {String} sB 開始（HH:mm）
     * @param {String} eB 終了（HH:mm）
     */
    timeOverlap(sA, eA, sB, eB) {
      const toNum = t => Number(t.replace(':', ''))
      return toNum(sA) < toNum(eB) && toNum(sB) < toNum(eA)
    },

    /**
     * 雇用指示の取得時の元データ（無ければ null）
     * AKBS登録済みの行は sequenceNumber、契約内容変更で足した行は changeFromSequenceNumber で元データと結ぶ
     * どちらも無い行は物件追加などの新規追加なので元データは無い
     * @param {Object} row 雇用指示
     */
    originalEmployment(row) {
      const sequenceNumber = row.sequenceNumber || row.changeFromSequenceNumber
      if (!sequenceNumber) {
        return null
      }
      const list = (this.akbs.originalData && this.akbs.originalData.employment) || []
      return list.find(e => e.sequenceNumber == sequenceNumber) || null
    },

    /**
     * 雇用指示の行の呼び名（メッセージ用）
     * @param {Number} index 行の位置
     */
    employmentName(index) {
      const row = (this.akbs.employment || [])[index] || {}
      // 物件名は画面では2行（物件名＋棟など）で出しているが、ダイアログの文では1行にする
      const site = (row.site || '').replace(/\r?\n/g, ' ').trim()
      return `雇用指示 ${index + 1}件目（${site || '物件未選択'}）`
    },

    /**
     * 勤務を直せない行で矛盾が見つかったときに、エラー文の後ろに足す一言（どの操作で直せるか）
     * @param {Object} row 雇用指示の行
     */
    fixHint(row) {
      if (row.mode === 'changeWage') {
        return 'この行は賃金のみ変更中です。賃金のみ変更を取り消してから、上書きで直してください。'
      }
      return 'この行はロック中です。上書きで直してください。'
    },

    /**
     * 賃金の並びの矛盾（ダイアログは出さず、見つかった分を全部返す）
     * 各賃金は終了日が開始日より前でないこと、前の賃金より開始日が後で、前の賃金の終了日と重ならないこと
     * 開いたときの差し替えで並びが崩れることがあるので、開いた直後（全部を知らせる）と保存時（最初の1件で止める）の両方で使う
     * @param {Array} list 雇用指示の行
     * @param {Number} onlyIndex この行だけ見るとき（省略なら全行）
     * @returns [{ title, message, keys: 赤くする欄のキー }]
     */
    wageOrderErrors(list, onlyIndex) {
      const errors = []
      for (let i = 0; i < list.length; i++) {
        if (onlyIndex !== undefined && i !== onlyIndex) {
          continue
        }
        const name = this.employmentName(i)
        const wages = list[i].wages || []
        for (let wi = 0; wi < wages.length; wi++) {
          const wage = wages[wi]
          const key = `employment.${i}.wage${wi}.`
          if (wi === 0 || !wage.startDate) {
            continue
          }
          const prev = wages[wi - 1]
          const prevKey = `employment.${i}.wage${wi - 1}.`
          if (prev.startDate && wage.startDate <= prev.startDate) {
            errors.push({ title: name + 'の賃金の適用日が不正', message: `賃金の適用日（${wage.startDate}）が、その前の賃金の適用日（${prev.startDate}）より前か同じ日になっています。\n賃金の並びを確認してください。`, keys: [key + 'startDate', prevKey + 'startDate'] })
          }
        }
      }
      return errors
    },

    /**
     * 今日以降に効く最低賃金（営業所（東京／千葉） → 行の配列。適用中と予定。開始日の古い順）。最低賃金マスタ（賃金画面）から取る
     * 取れなければ空（比べない）
     */
    async getMinWageRows() {
      const map = {}
      try {
        const res = await backend.searchData('akbsWage/getMinWageList', {})
        for (const m of (res.data && res.data.data) || []) {
          if ((m.isCurrent || m.isFuture) && m.salesOffice) {
            if (!map[m.salesOffice]) {
              map[m.salesOffice] = []
            }
            map[m.salesOffice].push(m)
          }
        }
        for (const office of Object.keys(map)) {
          map[office].sort((a, b) => (a.startDate < b.startDate ? -1 : 1))
        }
      } catch (error) {
        console.log(error)
      }
      return map
    },

    /**
     * 表示用の日付（yyyy/mm/dd。無ければ空）
     * @param {String} day 日付
     */
    slash(day) {
      return day ? moment(day).format('YYYY/MM/DD') : ''
    },

    /**
     * NGの欄を覚えて false を返す（validate の中で return this.ng(...) と書けるように）
     * @param  {...String} keys NGの欄のキー
     */
    ng(...keys) {
      this.errorKeys = keys
      return false
    },

    /**
     * エラーのダイアログ（入力チェックで止めたことを控える。register で承認者への一言を足すのに使う）
     */
    errorDialog(title, message) {
      this.validateError = true
      return dialogs.showErrorDialog(title, message)
    },

    /**
     * 承認者が入力チェックのエラーで止まったとき：この画面では直せないので、差戻してもらうよう伝える
     * 確認ダイアログをキャンセルしただけのときは出さない（validateError で区別）
     */
    async approverCannotFix() {
      if (!this.isApprover || !this.validateError) return
      await dialogs.showInfoDialog('承認者はこの画面で直せません', '申請のあとに AKBS の内容が変わるなどして、申請どおりに登録できなくなっています。\n差戻して、申請者に直してもらってください。')
    },

    /**
     * 保存時の矛盾チェックでNGになった欄か（赤い枠にする）
     * @param {Object} section 大項目（SECTIONS の1要素）
     * @param {Object} item 定義（ITEMS の定義配列の1要素）
     * @param {Number} index 明細の位置
     */
    isNg(section, item, index) {
      return this.errorKeys.includes(`${section.key}.${index}.${item.key}`)
    },

    /**
     * フリガナを半角カタカナにする
     * ひらがなで打たれていても、先にカタカナへ直してから半角にする（ひらがなには半角が無いため）
     * @param {String} text フリガナ
     */
    toHankakuKana(text) {
      return utils.hankaku(utils.katakana(text))
    },

    // ------------------------------------------------------------
    // フリガナの自動入力（氏名を日本語入力で打ったとき、変換を確定する前のひらがなを拾う）
    // ・item.kanaTo のある欄（清掃員の氏名 → nameKanaStaff、緊急連絡先の氏名 → kana）だけ
    // ・フリガナが空のときだけ入れる。自動で入れたあとは、氏名を打ち続ける間は追いかける（手で直したら触らない）
    // ・貼り付け・英数字の直打ちは拾えない（そのときは手で入れてもらう）
    // ------------------------------------------------------------

    /**
     * その行のフリガナ自動入力の状態（無ければ作る）
     * buf：確定した読み（ひらがな）、cur：変換中の読み、active：この入力でフリガナを入れてよいか、last：最後に自動で入れた値
     */
    kanaState(row) {
      let s = this.kanaStates.get(row)
      if (!s) {
        s = { buf: '', cur: '', active: false, last: '' }
        this.kanaStates.set(row, s)
      }
      return s
    },

    /**
     * 変換が始まった。フリガナが空か、前に自動で入れたままなら、この入力でフリガナを入れる
     */
    kanaStart(item, row) {
      if (!item.kanaTo) {
        return
      }
      const s = this.kanaState(row)
      const kana = String(row[item.kanaTo] || '')
      s.active = kana === '' || (s.last !== '' && kana === s.last)
      s.cur = ''
      // 氏名が空から打ち始めるなら読みもやり直し
      if (!String(row[item.key] || '')) {
        s.buf = ''
      }
    },

    /**
     * 変換中。ひらがな（と長音・スペース）だけの間はその読みを控え、漢字に変わったら直前のひらがなを残す
     */
    kanaUpdate(item, row, event) {
      if (!item.kanaTo) {
        return
      }
      const s = this.kanaState(row)
      if (!s.active) {
        return
      }
      const text = String(event.data || '')
      // \s は全角スペース（U+3000）も含む
      if (/^[ぁ-んー\s]*$/.test(text)) {
        s.cur = text
      }
    },

    /**
     * 変換が確定した。読みをためてフリガナの欄に半角カナで入れる
     */
    kanaEnd(item, row, event) {
      if (!item.kanaTo) {
        return
      }
      const s = this.kanaState(row)
      if (!s.active) {
        return
      }
      // 確定が空（取り消し）なら何も足さない
      if (String(event.data || '') !== '') {
        s.buf += s.cur
      }
      s.cur = ''
      this.kanaApply(item, row, s)
    },

    /**
     * 変換を介さない入力（スペース・削除など）。姓と名の間のスペースは読みにも入れ、氏名を全部消したら読みも消す
     */
    kanaInput(item, row, event) {
      if (!item.kanaTo || event.isComposing) {
        return
      }
      const s = this.kanaState(row)
      const value = String(event.target.value || '')
      if (value === '') {
        s.buf = ''
        s.cur = ''
        if (s.active || String(row[item.kanaTo] || '') === s.last) {
          s.active = true
          this.kanaApply(item, row, s)
        }
        return
      }
      if (!s.active) {
        return
      }
      if (event.inputType === 'insertText' && /^\s$/.test(String(event.data || ''))) {
        s.buf += ' '
        this.kanaApply(item, row, s)
      }
    },

    /**
     * ためた読みを半角カナにしてフリガナの欄へ（自動で入れた値を控えて、手で直されたかを次に見分ける）
     */
    kanaApply(item, row, s) {
      const kana = this.toHankakuKana(s.buf).replace(/\s+/g, ' ').replace(/^ +/, '')
      s.last = kana
      if (row[item.kanaTo] !== kana) {
        this.$set(row, item.kanaTo, kana)
      }
    },

    /**
     * 申請時に足りない項目の一覧（無ければ空配列）
     * 入力形式が違う項目も含める
     */
    requiredErrors() {
      const errors = []

      for (const section of SECTIONS) {
        const rows = this.rows(section)
        for (let i = 0; i < rows.length; i++) {
          for (const item of this.items[section.key]) {
            const caption = this.plainCaption(item)
            const name = section.repeat ? `${section.caption} ${i + 1}件目の${caption}` : caption

            if (this.isEmpty(item, rows[i])) {
              errors.push(name)
            } else if (this.errText(item, rows[i])) {
              errors.push(`${name}（形式が違います）`)
            }
          }
        }
      }

      // 雇用指示は表の中で赤くしている欄を数える
      // 取り消した行（無かった扱い）と、変更前の行・変更前の賃金（履歴として残るだけ。AKBS の古い値に不備があってもこの申請では直せない）は数えない
      const empTable = this.$refs.form ? this.$refs.form.querySelectorAll('.emp-table tr:not(.cancelled):not(.before):not(.wage-before) .is-empty').length : 0
      if (empTable) {
        // どの雇用指示のどの欄かを出す（表の同じ条件で拾う。変更なしの行の欄は「修正（上書き）」で直す）。拾えなければ数だけ
        const details = this.$refs.employment && this.$refs.employment.emptyFieldErrors ? this.$refs.employment.emptyFieldErrors() : []
        if (details.length) {
          for (const d of details) {
            errors.push(`雇用指示 ${d}`)
          }
        } else {
          errors.push(`雇用指示の未入力 ${empTable}箇所`)
        }
      }

      return errors
    },

    /**
     * 大項目の明細一覧
     * 追加可能な大項目は明細の配列、それ以外は akbs 自身を1件だけ返す
     * @param {Object} section 大項目（SECTIONS の1要素）
     */
    rows(section) {
      return section.repeat ? this.akbs[section.key] : [this.akbs]
    },

    /**
     * 明細を追加
     * @param {Object} section 大項目（SECTIONS の1要素）
     */
    addRow(section) {
      this.akbs[section.key].push(createRow(section.key))
    },

    /**
     * 明細を削除（最後の1件は空にするだけで残す）
     * @param {Object} section 大項目（SECTIONS の1要素）
     * @param {Number} index 明細の位置
     */
    removeRow(section, index) {
      if (this.akbs[section.key].length === 1) {
        this.$set(this.akbs[section.key], 0, createRow(section.key))
      } else {
        this.akbs[section.key].splice(index, 1)
      }
    },

    /**
     * 見出しから補足（「（保存時に半角に変換します）」など）を外した項目名
     * @param {Object} item 定義（ITEMS の定義配列の1要素）
     */
    plainCaption(item) {
      return item.caption.replace(/（.*）/, '')
    },

    /**
     * 見出しの補足（「（保存時に半角に変換します）」の括弧の中）。無ければ空文字
     * @param {Object} item 定義（ITEMS の定義配列の1要素）
     */
    captionNote(item) {
      const m = item.caption.match(/（(.*)）/)
      return m ? m[1] : ''
    },

    /**
     * 表の1行を使う項目か（備考など、幅いっぱい（w-full）の項目）
     * @param {Object} item 定義（ITEMS の定義配列の1要素）
     */
    isFullWidth(item) {
      return String(item.class || '').split(' ').includes('w-full')
    },

    /**
     * 入社取消のチェックを押せる期間か（入社日から1ヶ月以内。1ヶ月後の同じ日まで押せる）
     * 入社日が無ければ押せない
     */
    canCancelHire() {
      const dateHire = this.akbs.dateHire
      if (!dateHire) {
        return false
      }
      return utils.getToday() <= moment(dateHire).add(1, 'month').format('YYYY-MM-DD')
    },

    /**
     * チェックボックスを押せなくするか
     * 入社取消は入社日から1ヶ月を過ぎたら新しく付けられない。既に付いているものは外せるよう、そのときは押せる
     * @param {Object} item 定義（ITEMS の定義配列の1要素）
     * @param {Object} row 入力値
     */
    checkboxDisabled(item, row) {
      if (item.key !== 'isCancelled') {
        return false
      }
      return !row[item.key] && !this.canCancelHire()
    },

    /**
     * 押せないチェックボックスに出す説明
     */
    checkboxTip(item, row) {
      if (!this.checkboxDisabled(item, row)) {
        return ''
      }
      if (!this.akbs.dateHire) {
        return '入社日を入れると押せます。'
      }
      return `入社日（${this.akbs.dateHire}）から1ヶ月を過ぎているため、入社取消はできません。`
    },

    /**
     * 閲覧のみのときに入力欄の代わりに出す文字（未入力なら空文字）
     * 選択は選択肢の表示名、数値は桁区切りと単位。生年月日の和暦・年齢は枠の下に別で出す（subText）
     * @param {Object} item 定義（ITEMS の定義配列の1要素）
     * @param {Object} row 入力値（akbs または明細1件）
     */
    viewText(item, row) {
      const value = row[item.key]
      if (this.isBlank(value)) {
        return ''
      }

      if (item.type === 'select') {
        const found = (item.selections || []).find(s => s.value === String(value))
        return found ? found.text : String(value)
      }

      if (item.type === 'number') {
        return Number(value).toLocaleString() + (item.unit || '')
      }

      return String(value)
    },

    /**
     * 再入社のときに出す、前回の退職の項目の表示文字（チェックは あり／なし、未入力は －）
     * @param {Object} item 定義（ITEMS.retire の1要素）
     */
    previousRetireText(item) {
      const value = (this.akbs.previousRetire || {})[item.key]
      if (item.type === 'checkbox') {
        return value ? 'あり' : 'なし'
      }
      return this.viewText(item, this.akbs.previousRetire || {}) || '－'
    },

    /**
     * 前回の退職の項目のうち、承認者に目立たせるもの（再入社NG・入社取り消しにチェックが入っていたとき）
     * @param {Object} item 定義（ITEMS.retire の1要素）
     */
    isPreviousRetireWarning(item) {
      return item.type === 'checkbox' && !!(this.akbs.previousRetire || {})[item.key]
    },

    /**
     * 入力形式エラー（入力欄の右に赤字で出す。未入力・正しい形式なら空文字）
     * @param {Object} item 定義（ITEMS の定義配列の1要素）
     * @param {Object} row 入力値（val または明細1件）
     */
    errText(item, row) {
      const value = row[item.key]
      if (!value) {
        return ''
      }

      // 携帯番号 3桁-4桁-4桁
      if (item.format === 'mobile') {
        const pattern = /^[0-9]{3}-[0-9]{4}-[0-9]{4}$/
        if (!pattern.test(value)) {
          return '【3桁-4桁-4桁】の形式で入力してください'
        }
      }

      // 郵便番号 3桁-4桁
      if (item.format === 'zip') {
        const pattern = /^[0-9]{3}-[0-9]{4}$/
        if (!pattern.test(value)) {
          return '【3桁-4桁】の形式で入力してください'
        }
      }
      return ''
    },

    /**
     * テキスト欄の値を入れる。電話番号（type: tel）と〒（format: zip）は半角にする（全角の数字・ハイフンのほか、長音「ー」もハイフンに。前後の空白は除く）
     * @param {Object} item 定義（ITEMS の定義配列の1要素）
     * @param {Object} row 入力値（val または明細1件）
     * @param {String} value 入力された値
     */
    setText(item, row, value) {
      // WmsTextInput は読み込みで値が入ったときも change を出すので、同じ値ならそのまま（読み込んだ値を勝手に半角にして「変更」にしない）
      if (value === row[item.key]) {
        return
      }
      let v = value
      if ((item.type === 'tel' || item.format === 'zip') && typeof v === 'string') {
        // hankaku は「ー」を半角の「ｰ」にするので、それもハイフンにする
        v = utils.hankaku(v).replace(/[ーｰ－‐‑‒–—―]/g, '-').trim()
      }
      if (row[item.key] !== v) {
        this.$set(row, item.key, v)
      }
    },

    /**
     * 入力欄の右に出す補足テキスト（未入力なら空文字）
     * @param {Object} item 定義（ITEMS の定義配列の1要素）
     * @param {Object} row 入力値（val または明細1件）
     */
    subText(item, row) {
      const value = row[item.key]
      if (item.sub !== 'birthday' || !value) {
        return ''
      }

      const age = this.age(value)
      return age === null ? '' : `${utils.japaneseDate(value)}（${age}歳）`
    },

    /**
     * 生年月日から年齢を求める
     * @param {String} birthday 生年月日（YYYY-MM-DD）
     * @param {String} baseDate いつ時点の年齢か（YYYY-MM-DD。省略なら今日）
     */
    age(birthday, baseDate) {
      const birth = new Date(birthday)
      if (isNaN(birth.getTime())) {
        return null
      }

      const today = baseDate ? new Date(baseDate) : new Date()
      if (isNaN(today.getTime())) {
        return null
      }
      const month = today.getMonth() - birth.getMonth()
      let age = today.getFullYear() - birth.getFullYear()

      // 誕生日がまだ来ていない年は1歳引く
      if (month < 0 || (month === 0 && today.getDate() < birth.getDate())) {
        age--
      }

      return age
    },

    /**
     * Enterキーで次の入力欄へフォーカスを移す
     * 閉じている大項目の入力欄は飛ばす
     * @param {Object} e キーイベント（target が現在の入力欄）
     */
    nextField(e) {
      const inputs = Array.from(this.$refs.form.querySelectorAll('input:enabled'))
        .filter(input => {
          const accordion = input.closest('.accordion')
          return !accordion || accordion.classList.contains('open')
        })

      const index = inputs.indexOf(e.target)
      if (index < 0) {
        return
      }

      const next = inputs[index + 1]
      if (next) {
        next.focus()
      }
    },

    /**
     * 必須項目か（requiredKey を持つ項目は、その項目に入力があるときだけ必須）
     * @param {Object} item 定義（ITEMS の定義配列の1要素）
     * @param {Object} row 入力値（val または明細1件）
     */
    isRequired(item, row) {
      if (item.requiredKey) {
        return !this.isBlank(row[item.requiredKey])
      }

      return !!item.required
    },

    /**
     * 必須項目が未入力か
     * @param {Object} item 定義（ITEMS の定義配列の1要素）
     * @param {Object} row 入力値（val または明細1件）
     */
    isEmpty(item, row) {
      if (!this.isRequired(item, row)) {
        return false
      }

      return this.isBlank(row[item.key])
    },

    /**
     * 未入力か
     * @param {*} value 入力値
     */
    isBlank(value) {
      return value === null || value === undefined || value === ''
    },

    /**
     * 取得時から値が変わったか（変わった欄は背景を黄色にする）
     * @param {Object} section 大項目（SECTIONS の1要素）
     * @param {Object} item 定義（ITEMS の定義配列の1要素）
     * @param {Number} index 明細の位置（追加できない大項目は 0）
     */
    isChanged(section, item, index) {
      const orig = this.akbs.originalData
      if (!orig) {
        return false
      }

      // 追加できる大項目は明細ごとに、それ以外は AKBS情報そのものと突き合わせる
      const before = section.repeat ? ((orig[section.key] || [])[index] || {})[item.key] : orig[item.key]
      const after = this.rows(section)[index][item.key]

      return this.toText(before) !== this.toText(after)
    },

    /**
     * 得意先へのメール送信履歴を取得（この申請の分をまとめて取る）
     */
    async getMailHistory() {
      this.mailHistory = await clientMailManager.getHistoryByRequestNo(this, this.rp.requestNo) || []
    },

    /**
     * 新規追加した物件か（契約内容変更で足した行は含めない）
     * @param {Object} row 対象の雇用指示
     */
    isNewSite(row) {
      return !row.sequenceNumber && !row.isChangedTo
    },

    /**
     * 連絡の種類（新規追加した物件は入社、それ以外は退職）
     * @param {Object} row 対象の雇用指示
     */
    contactType(row) {
      return this.isNewSite(row) ? '入社' : '退職'
    },

    /**
     * 連絡の対象日（新規追加は作業開始日、それ以外は作業終了日）
     * @param {Object} row 対象の雇用指示
     */
    contactDate(row) {
      return this.isNewSite(row) ? row.startDateWork : row.endDateWork
    },

    /**
     * この雇用指示について送った履歴
     * 同じ物件でも入社と退職は別の連絡なので、種類と対象日まで見て突き合わせる
     * @param {Object} row 対象の雇用指示
     */
    historyOf(row) {
      const contactType = this.contactType(row)
      const targetDate = this.contactDate(row)

      return this.mailHistory.filter(h => {
        return h.cdSite && h.cdSite == row.cdSite
          && h.contactType == contactType
          && h.targetDate == targetDate
      })
    },

    /**
     * 得意先へ連絡モーダルを開く
     * 得意先名と対象日（作業開始日または作業終了日）が無いと文面が作れないため、先に入れてもらう
     * @param {Boolean} isMail メールならtrue、FAXならfalse
     * @param {Object} row 対象の雇用指示
     */
    async openMessageForClient(isMail, row) {
      if (!row.contactClient1) {
        await this.errorDialog('得意先名がありません', '得意先名を入力してください。')
        return
      }

      // 対象日（いつからの話か）が無いと文面が作れないため、先に入れてもらう
      if (!this.contactDate(row)) {
        const caption = this.isNewSite(row) ? '作業開始日' : '作業終了日'
        await this.errorDialog(caption + 'がありません', '雇用指示の' + caption + 'を入力してください。')
        return
      }

      this.isMail = isMail
      this.contactRow = row
      this.messageForClient = true
    },

    /**
     * 追加できる大項目（家族・緊急連絡先）の中に、取得時から変わったところがあるか
     * 入力欄の変更だけでなく、明細を足した・消したも変更として見る
     * （消したときは現在の明細だけを回す isChanged では拾えないため、入力のある明細の数で比べる）
     * @param {Object} section 大項目（SECTIONS の1要素）
     */
    isSectionChanged(section) {
      const items = this.items[section.key]
      const rows = this.rows(section)
      const origRows = this.akbs.originalData[section.key] || []

      const filled = list => list.filter(row => items.some(item => !this.isBlank(row[item.key]))).length
      if (filled(rows) !== filled(origRows)) {
        return true
      }

      return rows.some((_, i) => items.some(item => this.isChanged(section, item, i)))
    },

    /**
     * 取得時から変わったところにフラグを立てる（承認・AKBS登録のときに、変わったところだけ本番へ書くため）
     * ・清掃員情報・連絡先・給与・退職・備考：項目ごとに「項目キー + Changed」を true にする（例：nameStaffChanged）
     * ・家族・緊急連絡先：中に1つでも変更があれば「大項目キー + Changed」を true にする（例：familyChanged）
     * ・雇用指示：変更のある行に changed を立てる（AkbsEmploymentTable の markChanged）
     * 変わっていないところにはプロパティ自体を付けない。呼ぶたびに立て直す
     */
    markChanged() {
      const akbs = this.akbs

      // 前回立てたフラグを消す
      for (const section of SECTIONS) {
        if (section.repeat) {
          delete akbs[section.key + 'Changed']
        } else {
          for (const item of this.items[section.key]) {
            delete akbs[item.key + 'Changed']
          }
        }
      }

      // 元データが無い（新規入社）ときは比較できないので立てない
      if (akbs.originalData) {
        for (const section of SECTIONS) {
          if (section.repeat) {
            if (this.isSectionChanged(section)) {
              akbs[section.key + 'Changed'] = true
            }
          } else {
            for (const item of this.items[section.key]) {
              if (this.isChanged(section, item, 0)) {
                akbs[item.key + 'Changed'] = true
              }
            }
          }
        }
      }

      if (this.$refs.employment) {
        this.$refs.employment.markChanged()
      }
    },

    /**
     * 突き合わせ用の文字列（未入力はすべて空文字に揃える）
     * @param {*} value 値
     */
    toText(value) {
      return this.isBlank(value) ? '' : String(value)
    },

    /**
     * 郵便番号から住所を取得して入力欄に入れる（Google の Geocoder を使う）
     * 連絡先：都道府県・市区町村・町名の3欄に分けて入れる（番地は郵便番号からは分からないので、町名の欄に続けて打ってもらう）
     * 緊急連絡先：住所の1欄に「都道府県 + 市区町村 + 町名」をまとめて入れる
     * @param {Object} section 大項目（SECTIONS の1要素）
     * @param {Object} row 入力値（akbs または明細1件）
     */
    async fillAddress(section, row) {
      const zip = (row.zip || '').trim()
      if (!/^[0-9]{3}-?[0-9]{4}$/.test(zip)) {
        await this.errorDialog('郵便番号を確認してください', '郵便番号を【3桁-4桁】の形式で入力してから押してください。')
        return
      }

      // 一瞬で返るので、画面全体を覆うローディングは出さない
      try {
        const address = await this.searchAddress(zip)
        if (!address) {
          await this.errorDialog('住所が見つかりません', `郵便番号 ${zip} に当たる住所が見つかりませんでした。`)
          return
        }

        if (section.key === 'address') {
          row.pref = address.pref
          row.city = address.city
          row.address = address.town
        } else {
          row.address = address.pref + address.city + address.town
        }
      } catch (e) {
        console.log(e)
        await this.errorDialog('住所の取得に失敗しました', 'もう一度お試しください。続く場合はシステムの管理者にお問い合わせください。')
      }
    },

    /**
     * 郵便番号から住所を検索（Google の Geocoder）
     * 結果の住所要素を並び順ではなく種類で見て、都道府県・市区町村・町名に分ける
     * @param {String} zip 郵便番号
     * @returns {Object|null} { pref, city, town }。見つからなければ null
     */
    searchAddress(zip) {
      // Google Maps の JS は public/index.html で全画面共通に読み込んでいる
      return new Promise((resolve, reject) => {
        const geocoder = new window.google.maps.Geocoder()
        geocoder.geocode({ address: zip, language: 'ja', region: 'jp' }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.ZERO_RESULTS) {
            resolve(null)
            return
          }
          if (status !== window.google.maps.GeocoderStatus.OK || !results || !results.length) {
            reject(new Error('Geocoder status: ' + status))
            return
          }

          // 種類ごとに要素を取り出す（例：administrative_area_level_1 = 都道府県、locality = 市区町村、sublocality_level_1 = 区、sublocality_level_2 以降 = 町名・丁目）
          const parts = {}
          for (const c of results[0].address_components) {
            for (const t of c.types) {
              parts[t] = c.long_name
            }
          }

          const pref = parts.administrative_area_level_1 || ''
          const city = (parts.locality || '') + (parts.sublocality_level_1 || '')
          const town = (parts.sublocality_level_2 || '') + (parts.sublocality_level_3 || '') + (parts.sublocality_level_4 || '')

          resolve(pref || city ? { pref, city, town } : null)
        })
      })
    },

  },
}
</script>
<style scoped>

/* ヘッダーの「AKBSに登録済み」の印（承認済み。ボタンの代わり） */
.registered-mark {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border: 2px solid #fff;
  border-radius: 9999px;
  background: #15803d;
  color: #fff;
  font-size: 0.875rem;
  font-weight: bold;
  white-space: nowrap;
}

/* 最低賃金を下回ったときに出す、その都道府県の最低賃金マスタ（赤枠の中に小さな表） */
.min-wage-box {
  width: fit-content;
  margin: 12px 0;
  padding: 10px 14px;
  border: 2px solid #fca5a5;
  border-radius: 6px;
  background: #fef2f2;
}

.min-wage-title {
  font-size: 0.875rem;
  font-weight: bold;
  color: #b91c1c;
}

.min-wage-text {
  margin: 2px 0 8px;
  font-size: 0.8rem;
  color: #1f2937;
}

.min-wage-table {
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.8rem;
  background: #fff;
  border: 1px solid #fca5a5;
  border-radius: 6px;
  overflow: hidden;
}

.min-wage-table th,
.min-wage-table td {
  padding: 4px 12px;
  text-align: center;
  border-bottom: 1px solid #fecaca;
}

.min-wage-table thead th {
  background: #fee2e2;
  color: #b91c1c;
  font-size: 0.75rem;
}

.min-wage-table tbody tr:last-child td {
  border-bottom: none;
}

/* 引っかかった最低賃金の行 */
.min-wage-table tr.hit td {
  background: #fff7ed;
  color: #b91c1c;
  font-weight: bold;
}

/* メモに何か書いてあるとき：見出しの札と、オレンジ色の欄（変更ありの黄色・作業終了のローズ色と見分ける）
   閲覧のみのときも、灰色の地・灰色の文字にせず同じ色で出す（承認者・総務が見落とさないように） */
.memo-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 0 8px;
  border-radius: 9999px;
  background: #ea580c;
  color: #fff;
  font-size: 0.75rem;
  line-height: 1.25rem;
}

.memo-filled >>> label {
  color: #c2410c;
}

.memo-filled >>> textarea,
.memo-filled >>> textarea:disabled {
  border: 2px solid #f97316;
  background: #fff7ed !important;
  color: #7c2d12;
  -webkit-text-fill-color: #7c2d12;
  font-weight: bold;
}

/* 契約詳細の申請のお知らせ。ふだんはグレー基調、作業終了がまだの契約があるときだけローズ色の枠（作業終了ボタンと同じ色み） */
/* 契約詳細の変更のお知らせ。見落とされないよう太いピンクの線で囲む（黄色は手で変えた欄の色と同じなので使わない。ユーザー指示）
   作業終了がまだの契約があるときは赤（ピンクより強い色にして見分ける） */
.con-notice {
  margin-top: 16px;
  padding: 10px 14px;
  border: 3px solid #ec4899;
  border-radius: 6px;
  background: #fdf2f8;
}

.con-notice.todo {
  border: 3px solid #dc2626;
  background: #fee2e2;
}

.con-notice-title {
  font-size: 0.95rem;
  font-weight: bold;
  color: #9d174d;
}

.con-notice.todo .con-notice-title {
  color: #9f1239;
}

.con-notice-count {
  margin-left: 12px;
  padding: 1px 8px;
  border-radius: 9999px;
  background: #e11d48;
  color: #fff;
  font-size: 0.75rem;
}

.con-notice-lead {
  margin-top: 4px;
  font-size: 0.8rem;
  color: #1f2937;
}

.con-notice-table {
  margin-top: 8px;
  border-collapse: collapse;
  font-size: 0.8rem;
  background: #fff;
}

.con-notice-table th,
.con-notice-table td {
  padding: 4px 12px;
  border: 1px solid #e5e7eb;
  text-align: left;
  color: #374151;
}

.con-notice-table thead th {
  background: #f3f4f6;
  font-size: 0.75rem;
  color: #4b5563;
}

.con-notice-end {
  font-weight: bold;
  color: #be123c;
}

/* 行ごとの変更点（長くなるので小さめ。行の中では折り返さない） */
.con-notice-details {
  font-size: 0.75rem;
  white-space: nowrap;
}

.con-notice-date {
  margin-left: 8px;
}

.con-notice-todo {
  font-weight: bold;
  color: #be123c;
}

.con-notice-open {
  margin-left: 8px;
  color: #4338ca;
  text-decoration: underline;
}

/* 入力欄が潰れて操作しづらくならないよう、画面の最小幅を決める */
.page-main {
  min-width: 1000px;
}

.accordion {
  margin: 0;
  max-height: 0;
  overflow: hidden;
  /* transition: all .5s; */
}

/* ---- 大項目を横に並べる（清掃員基本情報・連絡先・給与関連・退職・家族・緊急連絡先）。幅が足りなければ折り返す ---- */
/* 同じ行に並んだ大項目は、一番高いものに高さをそろえる（stretch）。中身は上詰め */
.sections {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0 16px;
}

.section-card {
  display: flex;
  flex-direction: column;
  flex: 1 1 440px;
  min-width: 400px;
  max-width: 100%;
}

/* 閉じている大項目は見出しだけの高さにする（開いているものに合わせて伸ばさない） */
.section-card.closed {
  align-self: flex-start;
}

/* ---- 項目の表（項目名の列＋値。登録済み一覧の清掃員情報（detail-grid）と同じ見え方。縦に1列） ---- */
/* 罫線は grid の隙間（gap）に下地の色を見せて引く */
.form-grid {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  gap: 1px;
  margin: 10px 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  background: #e5e7eb;
  font-size: 0.875rem;
}

/* 項目名の列（薄い灰色。値と同じ高さで上下中央） */
.fcap {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  background: #f9fafb;
  color: #4b5563;
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
}

/* 項目名の補足（「保存時に半角に変換します」など）。項目名の下に小さく、折り返してよい */
.fcap-note {
  margin-top: 2px;
  font-size: 0.65rem;
  font-weight: 400;
  line-height: 1.2;
  color: #6b7280;
  white-space: normal;
}

/* 値の列（入力欄） */
.fval {
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 5px 12px;
  background: #fff;
}

/* 値の列の入力欄は全部同じ幅（列いっぱい）にそろえる。定義の w-52・w-80 などの固定幅は使わない
   郵便番号の「住所を取得」とチェックボックス（self-end が付いている）は中身の幅のまま */
.fval > * {
  flex: 1 1 0;
  min-width: 0;
  width: auto !important;
  max-width: 100%;
}

.fval > .self-end {
  flex: 0 0 auto;
}

.fval > * + .self-end {
  margin-left: 8px;
}

/* 幅いっぱいの項目（備考など）は値の列いっぱいに */
.fval.full > div,
.fval.full > .w-full {
  width: 100%;
}

/* 部品が持つ見出しは隠す（項目名はこちらの列に出す）。見出しの下の余白もなくす
   WmsTextInput などの中の label は >>> で届かせる。この画面自身の label（閲覧のみ・日付・選択・チェック）は普通の指定 */
.fval >>> label {
  display: none !important;
}

.fval label {
  display: none !important;
}

.fval >>> .mt-1 {
  margin-top: 0 !important;
}

/* 閲覧のみの文字は入力欄と同じ場所に、枠なしで太字（登録済み一覧の値と同じ） */
.fval .view {
  min-height: 0;
  padding: 2px 0;
  border: none;
  background: transparent;
  font-weight: 700;
  color: #1f2937;
}

/* 変更あり・未入力・NG の色は入力欄に付けているので、閲覧のみの文字は行ごと塗る */
.changed .fval .view,
.required-empty .fval .view,
.ng .fval .view {
  padding: 2px 8px;
  border-radius: 4px;
}

.open {
  max-height: fit-content;
}

/* 再入社のときに退職の大項目に出す、前回の退職の内容（入力欄と見分けがつくよう薄い枠で囲む） */
.previous-retire {
  padding: 6px 10px;
  border: 1px dashed #d1d5db;
  border-radius: 0.25rem;
  background: #f9fafb;
  line-height: 1.5;
}

/* 物件が持っている得意先から書き換えられた欄。誰が見ても分かるよう元の値を添える */
.edited {
  margin-top: 2px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.3;
  color: #c2410c;
}

/* 得意先への送信履歴 */
.history-table {
  border-collapse: collapse;
}

.history-table th,
.history-table td {
  border: 1px solid #bbf7d0;
  padding: 3px 10px;
  color: #4b5563;
}

.history-table th {
  background: #f0fdf4;
  color: #15803d;
  font-weight: 400;
  white-space: nowrap;
}

/* 閲覧のみの範囲。fieldset の見た目（枠・余白・最小幅）は消す */
.readonly-area {
  display: block;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
}

/* 閲覧のみのときは、雇用指示の勤務形態（vue-multiselect）のようにクリックで開く部品も止める
   （fieldset の disabled は input・select・button にしか効かないため） */
.readonly >>> .multiselect {
  pointer-events: none;
}

/* 閲覧のみのときに入力欄の代わりに出す文字
   disabled の入力欄は Chrome で文字を選べずコピーできないため、入力欄と同じ高さ・枠の文字にして選べるようにする */
.view {
  min-height: 2.25rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  background: #f9fafb;
  color: #374151;
  user-select: text;
}

/* 見出しに出す変更件数。入力欄の黄色と同じ色の系統で、そこに変更があることを示す */
.changed-count {
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #fef9c3;
  color: #b9601e;
  font-size: 11px;
  font-weight: 700;
}
/* 帯の「承認後に登録済み一覧にて編集可能」の注意書き。見出しより小さく、折り返さない */
.after-approval-note {
  margin-left: 12px;
  color: #dc2626;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}

/* 取得時から値が変わった入力欄は背景を黄色にする（どこを直したのか一目で分かるように）
   button は対象にしない（〒の横の「住所を取得」ボタンまで色が変わってしまうため） */
.changed >>> input:not([type='checkbox']),
.changed >>> textarea,
.changed >>> select,
.changed .view {
  background-color: #fef9c3 !important;
}

/* 変わっていても未入力なら、そちらを先に直してほしいので赤を優先する（この順番で上書きする） */
.required-empty >>> input:not([type='checkbox']),
.required-empty >>> textarea,
.required-empty >>> select,
.required-empty .view {
  background-color: #fee2e2 !important;
}

/* 保存時の矛盾チェックでNGになった欄は、見出しを赤字にして入力欄に赤い枠を付ける
   未入力の薄い赤（背景）とは別ものと分かるよう、枠にしている */
.ng >>> label {
  color: #dc2626 !important;
}

.ng >>> input:not([type='checkbox']),
.ng >>> textarea,
.ng >>> select,
.ng .view {
  box-shadow: inset 0 0 0 2px #dc2626 !important;
}

/* 標準の日付入力を他の入力欄と同じ高さ・字送りに寄せる */
input[type='date'] {
  line-height: 1.25rem;
}

input[type='date']::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.5;
}

input[type='date']:hover::-webkit-calendar-picker-indicator {
  opacity: 1;
}
</style>